/**
 * POST a GraphQL query to the Storefront API.
 * @param {string} query - The GraphQL document to send.
 * @param {function} [callBack] - Called with the parsed JSON response.
 * @param {function} [callBackError] - Called with no arguments on transport/parse failure.
 * @param {{version?: string, transaction?: object}} [options] - API version override (default `2024-07`) and internal cart transaction token.
 */
function fetchGraphQL(query, callBack, callBackError, options) {
  const version = options?.version || '2024-07';
  const operation = () => fetch(`/api/${version}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': window.EnrouteStorefront.token,
    },
    body: JSON.stringify({
      query: query,
    }),
  })
    .then((response) => response.json())
    .then((json) => {
      if (typeof callBack === 'function') {
        callBack(json);
      }
    })
    .catch((error) => {
      console.error('Błąd podczas pobierania danych:', error);
      if (typeof callBackError === 'function') {
        callBackError();
      }
    });
  return /\bmutation\b/.test(query) && window.enrouteCart?.trackCartMutation
    ? window.enrouteCart.trackCartMutation(operation, options?.transaction)
    : operation();
}

window._erj_ = window._erj_ || {};
window._erj_.fetchGraphQL = fetchGraphQL;

// Explicit mutation entry point; ordinary fetch calls remain untouched.
window._erj_.fetchCartMutation = (url, options) => {
  const operation = () => fetch(url, options);
  return window.enrouteCart?.trackCartMutation
    ? window.enrouteCart.trackCartMutation(operation)
    : operation();
};

/** Shared gift rules, picker interactions and cart offer transactions. */
(() => {
  const api = window._erj_ = window._erj_ || {};
  const text = (key, values) => api.cartOfferText(key, values);
  const variantId = (id) => String(id || '').replace(/^gid:\/\/shopify\/ProductVariant\//i, '');
  const codeKey = (code) => String(code || '').trim().toUpperCase();
  const appliedCodes = (cart) => (cart?.discountCodes || []).filter((entry) => entry?.applicable)
    .map((entry) => String(entry.code || '').trim()).filter(Boolean);
  const lineId = (item) => variantId(item.variant_id || item.id);
  const isOfferGiftLine = (item) => Boolean(codeKey(item?.properties?._reward_offer_code)) ||
    ['gift-selection', 'rewards_page_coupon'].includes(item?.properties?._from);
  const planId = (item) => item.selling_plan_allocation?.selling_plan?.id;
  const propertiesKey = (properties) => JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  const sameLine = (item, reference) => lineId(item) === lineId(reference) &&
    propertiesKey(item.properties) === propertiesKey(reference.properties) &&
    String(planId(item) || '') === String(planId(reference) || '');
  api.nativeGiftDiscountEndpoint ??= 'https://api.enroutejewelry.com/get_gifts_by_codes';
  let busy = false;
  let activePicker = null;

  function normalizeRule(rule) {
    if (rule?.isGiftCode !== true || typeof rule.code !== 'string' || !codeKey(rule.code) || !Array.isArray(rule.products) || !rule.products.length) return null;
    const available = (value) => value === true || value === 1 || (typeof value === 'string' && value.toLowerCase() === 'true');
    if (!rule.products.every((product) => typeof product?.productId === 'string' && product.productId && typeof product.title === 'string' &&
        Array.isArray(product.variants) && product.variants.length && product.variants.every((variant) =>
          typeof variant?.variantId === 'string' && variant.variantId && typeof variant.title === 'string'))) return null;
    return { ...rule, code: codeKey(rule.code), products: rule.products.map((product) => {
      const productFlag = product.availableForSale ?? product.available;
      return { ...product, id: product.productId, variants: product.variants.map((variant) => ({
        ...variant, id: variant.variantId,
        available: (productFlag == null || available(productFlag)) && available(variant.availableForSale ?? variant.available ?? productFlag),
      })) };
    }) };
  }

  async function lookup(codes, signal) {
    const endpoint = api.nativeGiftDiscountEndpoint;
    if (!endpoint) return [];
    const response = await fetch(endpoint, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        codes,
        country: window.Shopify.localizationHVG.countryIso,
        language: window.Shopify.localizationHVG.languageIso,
      }), signal,
    });
    if (!response.ok) throw new Error('Gift discount lookup failed');
    const result = await response.json();
    if (result?.success !== true || !Array.isArray(result.data)) throw new Error('Invalid gift discount response');
    const seen = new Set();
    return result.data.map(normalizeRule).filter((rule) => {
      if (!rule || !codes.includes(rule.code) || seen.has(rule.code)) return false;
      seen.add(rule.code);
      return true;
    });
  }

  async function readCart() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store' });
    if (!response.ok) throw new Error(text('cart_read_failed'));
    const cart = await response.json();
    if (!Array.isArray(cart?.items)) throw new Error(text('cart_read_failed'));
    return cart;
  }

  const freshCart = (transaction) => window.enrouteCart?.ensureCartFresh?.(transaction) || readCart();
  async function mutate(endpoint, body, transaction) {
    const result = await api.cartSections.mutateCart(endpoint, body, { withSections: false, transaction });
    if (!result?.ok) throw new Error(result?.error || text('gift_change_failed'));
    return result.data;
  }

  async function updateCodes(codes, transaction) {
    const token = window.enrouteCart?.state?.token;
    if (!token) throw new Error(text('cart_unavailable'));
    const query = `mutation {
      cartDiscountCodesUpdate(cartId: ${JSON.stringify(`gid://shopify/Cart/${token}`)}, discountCodes: ${JSON.stringify(codes)}) {
        cart { discountCodes { applicable code } }
        warnings { code message }
        userErrors { field message }
      }
    }`;
    const response = await new Promise((resolve, reject) => {
      api.fetchGraphQL(query, resolve, () => reject(new Error(text('code_update_failed'))), { version: '2025-07', transaction });
    });
    const result = response?.data?.cartDiscountCodesUpdate;
    if (response?.errors?.length || !result?.cart || result.userErrors?.length) throw new Error(text('code_update_failed'));
    return result;
  }

  async function removeGifts(matches, removed = [], transaction) {
    let cart = await readCart();
    const count = cart.items.filter(matches).length;
    for (let remaining = count; remaining > 0; remaining--) {
      const index = cart.items.findIndex(matches);
      if (index < 0) break;
      const item = cart.items[index];
      await mutate('change.js', { line: index + 1, quantity: 0 }, transaction);
      removed.push(item);
      // Deleting a line shifts positions and may change discount-based keys.
      if (remaining > 1) cart = await readCart();
    }
  }

  function restoreGift(gift, transaction) {
    const item = { id: lineId(gift), quantity: gift.quantity, properties: gift.properties || {} };
    if (planId(gift)) item.selling_plan = planId(gift);
    return mutate('add.js', { items: [item] }, transaction);
  }

  async function changeGiftCode(gift, code, transaction) {
    const cart = await readCart();
    const index = cart.items.findIndex((item) => sameLine(item, gift));
    if (index < 0) throw new Error(text('gift_changed'));
    const item = cart.items[index];
    const properties = { ...item.properties };
    if (code) properties._reward_offer_code = code;
    else delete properties._reward_offer_code;
    await mutate('change.js', { line: index + 1, quantity: item.quantity, properties }, transaction);
    return { ...item, properties };
  }

  async function run(operation) {
    if (busy) throw new Error(text('retry_later'));
    busy = true;
    api.offerSwitchInProgress = true;
    try {
      const owner = window.enrouteCart;
      return await (owner?.runCartTransaction ? owner.runCartTransaction(operation) : operation());
    } finally {
      // Revalidate legacy free gifts against the final cart, after all writes settle.
      api.offerSwitchInProgress = false;
      try { await window.enrouteCart?.requestCartUpdate?.(); }
      finally { busy = false; }
    }
  }

  async function rollback(error, actions) {
    const failures = [];
    for (const action of actions) {
      try { await action(); } catch (failure) { failures.push(failure); }
    }
    if (failures.length) throw new AggregateError([error, ...failures], text('gift_rollback_failed'));
    throw error;
  }

  function apply(code, selectedVariantId, options) {
    return run(async (transaction) => {
      const cart = await freshCart(transaction);
      const previousCodes = appliedCodes(cart);
      const oldCodes = previousCodes.filter((oldCode) => codeKey(oldCode) !== codeKey(code));
      const selectedId = variantId(selectedVariantId);
      const candidates = selectedId ? cart.items.filter((item) => lineId(item) === selectedId && item.quantity > 0) : [];
      const existingItem = candidates.find((item) => codeKey(item.properties?._reward_offer_code) === codeKey(code)) ||
        candidates.find(isOfferGiftLine) || candidates[0];
      let addedGift;
      let transferredGift;
      const removedGifts = [];
      const applyCode = async () => {
        const result = await updateCodes([code], transaction);
        const discount = result.cart.discountCodes?.find((item) => codeKey(item.code) === codeKey(code));
        const warnings = result.warnings || [];
        if (!discount?.applicable) {
          const error = new Error(text('offer_not_applicable'));
          error.warningCode = warnings[0]?.code || 'OTHER_REASON';
          throw error;
        }
      };
      try {
        // Add the selected gift before applying the offer, regardless of entry point.
        if (selectedId && !existingItem) {
          const item = { id: selectedId, quantity: 1, properties: { _from: options?.giftSource || 'gift-selection', _reward_offer_code: code } };
          const data = await mutate('add.js', { items: [item] }, transaction);
          addedGift = data?.items?.[0] || item;
        }
        await applyCode();
        if (isOfferGiftLine(existingItem) &&
                   codeKey(existingItem.properties._reward_offer_code) !== codeKey(code)) {
          transferredGift = await changeGiftCode(existingItem, code, transaction);
          await applyCode();
        }
        const hasStaleGift = selectedId && cart.items.some((item) =>
          codeKey(item.properties?._reward_offer_code) === codeKey(code) && lineId(item) !== selectedId);
        if (oldCodes.length || hasStaleGift) {
          const oldGiftHandles = new Set((window.accountInfo?.codeRecords || []).filter((record) =>
            oldCodes.some((oldCode) => codeKey(oldCode) === codeKey(record.code)) &&
            ['free_product', 'product_variant', 'product_variants'].includes(record.extra?.type || record.type)
          ).flatMap((record) => (record.extra?.products || []).map((product) => product.handle)).filter(Boolean));
          await removeGifts((item) => {
            const giftCode = codeKey(item.properties?._reward_offer_code);
            if (selectedId && giftCode === codeKey(code)) return lineId(item) !== selectedId;
            if (giftCode) return oldCodes.some((oldCode) => codeKey(oldCode) === giftCode);
            return isOfferGiftLine(item) && oldGiftHandles.has(item.handle);
          }, removedGifts, transaction);
          if (removedGifts.length) await applyCode();
        }
        return { previousCodes, previousCart: cart, reused: Boolean(existingItem), item: existingItem || addedGift };
      } catch (error) {
        const actions = removedGifts.map((gift) => () => restoreGift(gift, transaction));
        if (transferredGift) actions.push(() => changeGiftCode(transferredGift, existingItem.properties._reward_offer_code, transaction));
        if (addedGift) actions.push(() => removeGifts((item) => sameLine(item, addedGift), [], transaction));
        actions.push(() => updateCodes(previousCodes, transaction));
        return rollback(error, actions);
      }
    });
  }

  function changeVariant(code, previousVariantId, nextVariantId) {
    return run(async (transaction) => {
      const previousId = variantId(previousVariantId);
      const nextId = variantId(nextVariantId);
      if (previousId === nextId) return;
      const cart = await readCart();
      const oldItem = cart.items.find((item) => lineId(item) === previousId && codeKey(item.properties?._reward_offer_code) === codeKey(code)) ||
        cart.items.find((item) => lineId(item) === previousId && !item.properties?._reward_offer_code);
      if (!oldItem) throw new Error(text('gift_missing'));
      const target = { ...oldItem, variant_id: nextId, id: nextId };
      const quantityBefore = cart.items.find((item) => sameLine(item, target))?.quantity || 0;
      const item = { id: nextId, quantity: 1, properties: oldItem.properties || {} };
      if (planId(oldItem)) item.selling_plan = planId(oldItem);
      await mutate('add.js', { items: [item] }, transaction);
      try {
        const current = await readCart();
        const index = current.items.findIndex((line) => sameLine(line, oldItem));
        if (index < 0) throw new Error(text('gift_changed'));
        // Only the gifted unit changes variant; extra units on the old line stay.
        await mutate('change.js', { line: index + 1, quantity: current.items[index].quantity - 1 }, transaction);
      } catch (error) {
        return rollback(error, [async () => {
          const current = await readCart();
          const index = current.items.findIndex((line) => sameLine(line, target));
          if (index >= 0) await mutate('change.js', { line: index + 1, quantity: quantityBefore }, transaction);
        }]);
      }
    });
  }

  function cancel(codes) {
    return run(async (transaction) => {
      const previousCodes = appliedCodes(await freshCart(transaction));
      const removedGifts = [];
      try {
        await updateCodes(previousCodes.filter((code) => !codes.some((removed) => codeKey(removed) === codeKey(code))), transaction);
        await removeGifts((item) => codes.some((code) => codeKey(code) === codeKey(item.properties?._reward_offer_code)), removedGifts, transaction);
      } catch (error) {
        return rollback(error, [...removedGifts.map((gift) => () => restoreGift(gift, transaction)), () => updateCodes(previousCodes, transaction)]);
      }
    });
  }

  function findGift(rule, cart, includePaid = false) {
    const owned = cart?.items?.find((item) => codeKey(item.properties?._reward_offer_code) === codeKey(rule.code));
    if (owned || !includePaid) return owned;
    const ids = new Set(rule.products.flatMap((product) => product.variants.map((variant) => variantId(variant.id))));
    return cart?.items?.find((item) => !item.properties?._reward_offer_code && ids.has(lineId(item)));
  }

  function createPicker({ rule, switcher, toggle, popup, label, image, selectedId, onSelect, onChange, isBusy = () => false }) {
    let selected = null;
    const entries = rule.products.flatMap((product) => product.variants.map((variant) => ({ product, variant })));
    const close = () => {
      popup.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      document.removeEventListener('pointerdown', outside, true);
      if (popup.parentElement !== switcher) switcher.appendChild(popup);
      popup.removeAttribute('style');
      if (activePicker === close) activePicker = null;
    };
    const outside = (event) => { if (!popup.contains(event.target) && !toggle.contains(event.target)) close(); };
    const open = () => {
      if (!popup.hidden) return close();
      if (isBusy()) return;
      activePicker?.();
      document.body.appendChild(popup);
      popup.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      activePicker = close;
      document.addEventListener('pointerdown', outside, true);
    };
    const keydown = (event) => { if (event.key === 'Escape') { close(); toggle.focus(); } };
    // Restoring a focused popup to its switcher also fires focusout. It is
    // already hidden then; closing again would move it during appendChild.
    const focusout = (event) => { if (!popup.hidden && !popup.contains(event.relatedTarget)) close(); };
    const paint = (entry) => {
      if (!entry) return;
      selected = entry;
      const { product, variant } = entry;
      label.textContent = rule.products.length === 1 ? variant.title : `${product.title} — ${variant.title}`;
      if (image) {
        const url = variant.image?.url || product.image?.url || '';
        if (image.querySelector('img')?.getAttribute('src') !== url) {
          image.replaceChildren();
          if (url) {
            const img = document.createElement('img');
            img.src = url; img.alt = variant.image?.altText || product.image?.altText || product.title; img.loading = 'lazy';
            image.appendChild(img);
          }
        }
      }
      popup.querySelectorAll('[data-gift-option]').forEach((option) => {
        const selectedNow = option.dataset.variantId === variant.id;
        option.querySelector('[data-gift-check]').hidden = !selectedNow;
        option.closest('.hvg-cart-variant-switcher-popup-optionblock-item')?.classList.toggle('is-selected', selectedNow);
      });
      onChange?.(product, variant);
      close();
    };
    popup.anchorElement = toggle;
    popup.replaceChildren();
    for (const product of rule.products) {
      const group = document.createElement('div');
      group.className = 'hvg-cart-variant-switcher-popup-optionblock';
      const heading = document.createElement('div');
      heading.className = 'hvg-cart-variant-switcher-popup-optionblock-item-title';
      heading.textContent = product.title;
      group.appendChild(heading);
      for (const variant of product.variants) {
        const item = document.createElement('div');
        item.className = 'hvg-cart-variant-switcher-popup-optionblock-item';
        item.toggleAttribute('data-disabled', !variant.available);
        const option = document.createElement('button');
        option.type = 'button'; option.dataset.giftOption = ''; option.dataset.variantId = variant.id;
        option.className = `hvg-cart-variant-switcher-popup-optionblock-item-name${variant.available ? '' : '-disabled'}`;
        option.textContent = variant.available ? variant.title : text('variant_sold_out', { title: variant.title });
        option.disabled = !variant.available;
        option.setAttribute('aria-disabled', String(option.disabled));
        const check = document.createElement('span');
        check.className = 'hvg-cart-variant-switcher-popup-optionblock-item-check'; check.dataset.giftCheck = ''; check.hidden = true;
        option.appendChild(check);
        option.addEventListener('click', async () => {
          if (!variant.available || isBusy()) return;
          close();
          try { if (await onSelect?.(product, variant) !== false) paint({ product, variant }); }
          catch (error) { api.cartSections?.showError?.(error.message); }
        });
        item.appendChild(option); group.appendChild(item);
      }
      popup.appendChild(group);
    }
    toggle.addEventListener('click', open);
    popup.addEventListener('keydown', keydown);
    popup.addEventListener('focusout', focusout);
    paint(entries.find(({ variant }) => variantId(variant.id) === variantId(selectedId)) || entries.find(({ variant }) => variant.available));
    return {
      get selected() { return selected; },
      get available() { return entries.some(({ variant }) => variant.available); },
      select(id) { paint(entries.find(({ variant }) => variantId(variant.id) === variantId(id))); },
      close,
      destroy() { close(); toggle.removeEventListener('click', open); popup.removeEventListener('keydown', keydown); popup.removeEventListener('focusout', focusout); },
    };
  }

  api.cartOffers = { get busy() { return busy; }, variantId, appliedCodes, lookup, readCart, apply, changeVariant, cancel, findGift, createPicker };
})();

/** Use the popup itself as the container; its anchor selector is scoped to its parent. */
class AnchoredPopup extends HTMLElement {
  static get observedAttributes() {
    return ['hidden', 'anchor', 'fixed', 'min-width', 'max-height', 'placement', 'align', 'gap', 'margin'];
  }

  connectedCallback() {
    this._update = (event) => {
      // Internal option scrolling must not move the popup or reset its scroll.
      if (event && (event.target === this || this.contains(event.target))) return;
      this.reposition();
    };
    this._sync();
  }

  disconnectedCallback() {
    this._stop();
  }

  attributeChangedCallback() {
    if (this.isConnected && this._update) this._sync();
  }

  _stop() {
    window.removeEventListener('resize', this._update);
    window.removeEventListener('scroll', this._update, true);
    window.visualViewport?.removeEventListener('resize', this._update);
    window.visualViewport?.removeEventListener('scroll', this._update);
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
  }

  _sync() {
    this._stop();
    if (this.hidden) return;
    this.reposition();
    window.addEventListener('resize', this._update);
    window.addEventListener('scroll', this._update, true);
    window.visualViewport?.addEventListener('resize', this._update);
    window.visualViewport?.addEventListener('scroll', this._update);
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => this.reposition());
      this._resizeObserver.observe(this);
      if (this.anchorElement) this._resizeObserver.observe(this.anchorElement);
    }
  }

  set anchorElement(element) {
    this._anchorElement = element;
    if (this.isConnected && this._update) this._sync();
  }

  get anchorElement() {
    if (this._anchorElement) return this._anchorElement;
    const selector = this.getAttribute('anchor');
    return selector ? this.parentElement?.querySelector(selector) : null;
  }

  reposition() {
    const toggle = this.anchorElement;
    if (this.hidden || !toggle) return;
    const number = (name, fallback) => {
      const value = this.getAttribute(name);
      return value !== null && value.trim() !== '' && Number.isFinite(Number(value))
        ? Math.max(0, Number(value)) : fallback;
    };
    const fixed = this.hasAttribute('fixed');
    const minWidth = number('min-width', 0);
    const maxHeight = number('max-height', Infinity);
    const placement = this.getAttribute('placement') || 'bottom';
    const align = this.getAttribute('align') || 'start';
    const gap = number('gap', 0);
    const margin = number('margin', 8);
    const scrollTop = this.scrollTop;
    const viewport = window.visualViewport;
    const bounds = {
      left: viewport?.offsetLeft || 0,
      top: viewport?.offsetTop || 0,
      right: (viewport?.offsetLeft || 0) + (viewport?.width || document.documentElement.clientWidth),
      bottom: (viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight),
    };
    for (let parent = this.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
      const style = getComputedStyle(parent);
      const rect = parent.getBoundingClientRect();
      if (/auto|scroll|hidden|clip/.test(style.overflowX)) {
        bounds.left = Math.max(bounds.left, rect.left + parent.clientLeft);
        bounds.right = Math.min(bounds.right, rect.left + parent.clientLeft + parent.clientWidth);
      }
      if (/auto|scroll|hidden|clip/.test(style.overflowY)) {
        bounds.top = Math.max(bounds.top, rect.top + parent.clientTop);
        bounds.bottom = Math.min(bounds.bottom, rect.top + parent.clientTop + parent.clientHeight);
      }
    }
    bounds.left += margin;
    bounds.right -= margin;
    bounds.top += margin;
    bounds.bottom -= margin;
    const anchor = toggle.getBoundingClientRect();
    const availableWidth = Math.max(0, bounds.right - bounds.left);
    Object.assign(this.style, {
      position: fixed ? 'fixed' : 'absolute', boxSizing: 'border-box', overflow: 'auto',
      left: 'auto', top: 'auto', right: 'auto', bottom: 'auto',
      minWidth: '0', width: 'max-content', maxWidth: `${availableWidth}px`, maxHeight: 'none',
    });
    const width = Math.min(Math.max(anchor.width, minWidth, this.getBoundingClientRect().width), availableWidth);
    this.style.width = `${width}px`;
    const naturalHeight = this.getBoundingClientRect().height;
    const below = Math.max(0, bounds.bottom - Math.max(bounds.top, anchor.bottom + gap));
    const above = Math.max(0, Math.min(bounds.bottom, anchor.top - gap) - bounds.top);
    const preferredAbove = placement === 'top';
    const preferredSpace = preferredAbove ? above : below;
    const oppositeSpace = preferredAbove ? below : above;
    const flip = preferredSpace < Math.min(naturalHeight, maxHeight) && oppositeSpace > preferredSpace;
    const openAbove = flip ? !preferredAbove : preferredAbove;
    const height = Math.min(naturalHeight, maxHeight, openAbove ? above : below);
    this.style.maxHeight = `${height}px`;
    const anchorLeft = align === 'end' ? anchor.left + anchor.width - width
      : align === 'center' ? anchor.left + (anchor.width - width) / 2 : anchor.left;
    const left = Math.max(bounds.left, Math.min(anchorLeft, bounds.right - width));
    const top = Math.max(bounds.top, Math.min(openAbove ? anchor.top - gap - height : anchor.bottom + gap, bounds.bottom - height));
    const rect = this.getBoundingClientRect();
    this.style.left = `${fixed ? left : this.offsetLeft + left - rect.left}px`;
    this.style.top = `${fixed ? top : this.offsetTop + top - rect.top}px`;
    this.scrollTop = scrollTop;
  }
}

if (!customElements.get('anchored-popup')) customElements.define('anchored-popup', AnchoredPopup);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
if (!customElements.get('modal-opener')) customElements.define('modal-opener', ModalOpener);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    // Bind every close control (a dialog may have more than one, e.g. a × and a Cancel
    // shown on different breakpoints).
    this.querySelectorAll('[id^="ModalClose-"]').forEach((button) =>
      button.addEventListener('click', this.hide.bind(this, false))
    );
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    // Modals inside a section are relocated to <body> so a section's stacking/overflow
    // context can't trap them. Modals rendered directly in the layout stay in place.
    const section = this.closest('.shopify-section');
    if (section) {
      this.dataset.section = section.id.replace('shopify-section-', '');
      document.body.appendChild(this);
    }
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.hidden = false;
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    // todo：a11y
    // if (typeof trapFocus === 'function') trapFocus(this, this.querySelector('[role="dialog"]'));
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    // todo：a11y
    // if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
  }
}
if (!customElements.get('modal-dialog')) customElements.define('modal-dialog', ModalDialog);

// Forgotten-password modal: submit the recover form without a full page reload,
// swapping only the server-rendered form (success / error states come from Liquid).
class ForgottenModal extends ModalDialog {
  connectedCallback() {
    super.connectedCallback();
    // super() may relocate this element to <body>, which re-enters connectedCallback.
    // Use abortController as the "already bound" sentinel so the submit handler binds
    // exactly once across that move (and rebinds cleanly after a real disconnect).
    if (this.abortController) return;
    this.abortController = new AbortController();
    // Delegated: the submit event bubbles, so a form swapped in later is still caught
    // without re-binding.
    this.addEventListener('submit', this.onSubmit.bind(this), {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  async onSubmit(event) {
    const form = event.target.closest('form[action$="/account/recover"]');
    if (!form) return;
    event.preventDefault();

    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('loading');
      submitBtn.setAttribute('aria-busy', 'true');
    }

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const freshForm = doc.querySelector('#forgotten form[action$="/account/recover"]');
      if (freshForm) {
        // Swap only the form; the close button and .bottom stay intact.
        form.replaceWith(freshForm);
      } else {
        form.submit(); // Unexpected response → fall back to a native submit.
      }
    } catch (error) {
      form.submit(); // Network error → fall back to a native submit.
    }
  }
}
if (!customElements.get('forgotten-modal')) customElements.define('forgotten-modal', ForgottenModal);

// Mobile "added to cart" confirmation toast. Unlike ModalDialog this is a NON-blocking,
// auto-dismissing notification: no body scroll-lock, no backdrop, visibility is the `.active`
// class (which CSS only reveals under the mobile media query), and it self-closes after ~6s.
// It is driven imperatively — callers hold a reference and call show()/swapText() directly,
// e.g. `document.getElementById('mobileconfirmationtoast')?.show()`.
class MobileCartNotification extends HTMLElement {
  constructor() {
    super();
    this._dismissTimer = null;
    this._restoreTimer = null;
    this._originalTitle = null;
    this._onClick = this._onClick.bind(this);
    this._onCartUpdated = this._onCartUpdated.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._onClick);
    window.addEventListener('enroute:cart-updated', this._onCartUpdated);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    window.removeEventListener('enroute:cart-updated', this._onCartUpdated);
    clearTimeout(this._dismissTimer);
    clearTimeout(this._restoreTimer);
  }

  // Show the toast and (re)arm the auto-dismiss. Safe to call repeatedly — a fresh add resets
  // the timer instead of stacking timeouts.
  show() {
    this.hidden = false;
    this.classList.add('active');
    if (typeof showBorder === 'function') showBorder('top', 5000, this);
    clearTimeout(this._dismissTimer);
    this._dismissTimer = setTimeout(() => this.hide(), 6000);
  }

  hide() {
    clearTimeout(this._dismissTimer);
    this.classList.remove('active');
    this.hidden = true;
  }

  // Temporarily replace the title text (used to surface add-to-cart errors / stock limits),
  // restoring the original after ~5.5s. Repeated calls keep the true original, not swapped text.
  swapText(newText) {
    const titleEl = this.querySelector('.add-success-toast-mobile-title');
    if (!titleEl) return;
    if (this._originalTitle == null) this._originalTitle = titleEl.textContent;
    titleEl.textContent = newText;
    clearTimeout(this._restoreTimer);
    this._restoreTimer = setTimeout(() => {
      titleEl.textContent = this._originalTitle;
      this._originalTitle = null;
    }, 5500);
  }

  _onClick(event) {
    if (event.target.closest('[data-mobile-toast-close]')) {
      event.preventDefault();
      this.hide();
      return;
    }
    const redirect = event.target.closest('[data-mobile-toast-redirect]');
    if (redirect) {
      event.preventDefault();
      window.location.href = redirect.dataset.mobileToastRedirect;
    }
  }

  _onCartUpdated(event) {
    const countEl = this.querySelector('[data-mobile-toast-count]');
    const cart = event && event.detail && event.detail.cart;
    if (countEl && cart && typeof cart.item_count !== 'undefined') {
      countEl.textContent = cart.item_count;
    }
  }
}
if (!customElements.get('mobile-cart-notification'))
  customElements.define('mobile-cart-notification', MobileCartNotification);

// Desktop "added to cart" confirmation toast — a centered, auto-dismissing notification.
// CSS only reveals `.active` on desktop, except `.is-error` which shows on every viewport.
// Driven imperatively (formerly the Vue `showTemporaryMessage` on `$refs.asuccess`): callers
// hold a reference and call show(), e.g. `document.querySelector('add-success-toast')?.show()`.
class CartToast extends HTMLElement {
  constructor() {
    super();
    this._showTimer = null;
    this._hideTimer = null;
    this._errorTimer = null;
    this._originalHtml = null;
  }

  disconnectedCallback() {
    clearTimeout(this._showTimer);
    clearTimeout(this._hideTimer);
    clearTimeout(this._errorTimer);
  }

  // Briefly reveal the toast, then auto-hide. An optional `message` temporarily replaces the
  // text (the pristine markup is restored on hide); pass `{ error: true }` to force it visible
  // on mobile too. Mirrors the old showTemporaryMessage: fade in after 250ms, hide after `timeout`.
  show(message, timeout = 3000, { error = false } = {}) {
    clearTimeout(this._showTimer);
    clearTimeout(this._hideTimer);
    // Snapshot the pristine markup once so repeated calls never capture swapped-in text.
    if (this._originalHtml == null) this._originalHtml = this.innerHTML;
    else this.innerHTML = this._originalHtml;

    if (message) this.innerText = message;

    if (error) {
      this.classList.add('is-error');
      clearTimeout(this._errorTimer);
      this._errorTimer = setTimeout(() => this.classList.remove('is-error'), timeout);
    }

    this._showTimer = setTimeout(() => {
      this.hidden = false;
      this.classList.add('active');
      this._hideTimer = setTimeout(() => {
        this.classList.remove('active');
        this.hidden = true;
        this.innerHTML = this._originalHtml;
      }, Math.max(timeout - 250, 0));
    }, 250);
  }
}
if (!customElements.get('add-success-toast'))
  customElements.define('add-success-toast', CartToast);

// Reveals / hides the password field it sits next to. The element *is* the control, so it
// must be the input's next element sibling — the eye / eye-slash icon is picked by an
// `input[type="text"] + .show-pass` CSS rule, i.e. the input's type is the only state.
// role/tabindex are set here rather than in markup: without JS the control does nothing,
// so it should not be reachable.
class PasswordToggle extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.setAttribute('role', 'button');
    this.setAttribute('tabindex', '0');
    this.setAttribute('aria-pressed', String(this.input?.type === 'text'));

    this.addEventListener('click', this.onClick.bind(this), { signal });
    this.addEventListener('keydown', this.onKeydown.bind(this), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  get input() {
    const previous = this.previousElementSibling;
    return previous?.tagName === 'INPUT' ? previous : null;
  }

  onClick(event) {
    event.preventDefault();
    this.toggle();
  }

  onKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault(); // Space would otherwise scroll the page.
    this.toggle();
  }

  toggle() {
    const input = this.input;
    if (!input) return;

    const revealing = input.type === 'password';
    input.type = revealing ? 'text' : 'password';
    this.setAttribute('aria-pressed', String(revealing));
  }
}
if (!customElements.get('password-toggle')) customElements.define('password-toggle', PasswordToggle);

class PasswordMatch extends HTMLElement {
  connectedCallback() {
    this.password = this.querySelector('input[name="customer[password]"]');
    this.confirm = this.querySelector('input[name="customer[password_confirmation]"]');
    this.errorEl = this.querySelector('[data-password-error]');
    this.submit = this.querySelector('button[type="submit"]');

    if (!this.password || !this.confirm) return;

    this.onInput = this.validate.bind(this);
    this.password.addEventListener('input', this.onInput);
    this.confirm.addEventListener('input', this.onInput);
  }

  disconnectedCallback() {
    this.password?.removeEventListener('input', this.onInput);
    this.confirm?.removeEventListener('input', this.onInput);
  }

  validate() {
    const mismatch = this.confirm.value.length > 0 && this.password.value !== this.confirm.value;
    if (this.errorEl) this.errorEl.hidden = !mismatch;
    this.confirm.classList.toggle('error', mismatch);
    if (this.submit) this.submit.disabled = mismatch;
  }
}
if (!customElements.get('password-match')) customElements.define('password-match', PasswordMatch);

/**
 * Subscribe an email to the newsletter. Shared by the footer, the universal popup and the
 * SEO blog / author templates — each owns its own success and error UI, this only reports
 * whether the address got on the list.
 * @param {string} email
 * @returns {Promise<{status: 'success' | 'subscribed', followUp: number, code: string}>} - `subscribed`
 *   means the address was already on the list. `code` is the newly created discount
 *   code, only present when `followUp` is 0. Rejects on timeout (AbortError), transport
 *   failure, or an API response that reports no success.
 */
async function subscribeEmail(email) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20 * 1000);
  const params = new URLSearchParams(window.location.search);

  try {
    const response = await fetch('https://api.enroutejewelry.com/subscribe_v2', {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({
        email: email,
        isLoggedIn: Boolean(window._erj_?.customer?.id),
        utm_source: params.get('utm_source') || '',
        utm_medium: params.get('utm_medium') || '',
        utm_campaign: params.get('utm_campaign') || '',
      }),
      mode: 'cors',
      signal: controller.signal,
    });
    const result = await response.json();

    if (!result.data || !result.data.success || result.data.followUp === -1) {
      throw new Error(`[Newsletter] API request failed: ${JSON.stringify(result)}`);
    }

    // Guests have no identity anywhere else, so tie the address to the Klaviyo profile here.
    if (!window._erj_?.customer?.id) {
      localStorage.setItem('enroute.klaviyo_email', email);
      window.klaviyo?.identify?.({ email: email });
    }

    const followUp = result.data.followUp;
    return {
      status: [2, 3].includes(followUp) ? 'subscribed' : 'success',
      followUp: followUp,
      code: followUp === 0 ? (result.data.code || '') : '',
    };
  } finally {
    clearTimeout(timeoutId);
  }
}
window._erj_ = window._erj_ || {};
window._erj_.subscribeEmail = subscribeEmail;

// Footer newsletter sign-up. The result panels are pure CSS: the host carries `.newsletter`,
// and `sent` + `success` / `subscribed` reveal the matching panel while hiding the form
// (see the `footer .newsletter.sent` rules in global.css). Failures are logged, not shown —
// the footer has no error panel to show them in.
class JoinMembership extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.addEventListener('submit', this.onSubmit.bind(this), { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  async onSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const email = form.elements.email?.value;
    if (!email) return;

    const button = form.querySelector('button[type="submit"]');
    this.setLoading(button, true);

    try {
      const { status } = await window._erj_.subscribeEmail(email);
      form.reset();
      this.classList.add('sent', status);
    } catch (error) {
      console.error(
        error.name === 'AbortError'
          ? '[Newsletter] Request timed out. Please try again later:'
          : '[Newsletter] Subscription failed. Please try again:',
        error
      );
    } finally {
      this.setLoading(button, false);
    }
  }

  setLoading(button, loading) {
    if (!button) return;
    button.disabled = loading;
    button.classList.toggle('loading', loading);
  }
}
if (!customElements.get('join-membership')) customElements.define('join-membership', JoinMembership);

/**
 * Enroll an email in the back-in-stock notification list.
 * @param {string|number} productId
 * @param {string|number} variantId
 * @param {string} productHandle
 * @param {string} email
 * @param {Function} [onSuccess]
 * @param {Function} [onFail]
 * @param {string} [scenario] - Optional origin tag, e.g. 'wishlist'.
 */
async function subscribeBackToStock(productId, variantId, productHandle, email, onSuccess, onFail, scenario) {
  const url = 'https://api.enroutejewelry.com/back_in_stock_enroll';

  try {
    const payload = {
      email: email.toLowerCase(),
      productId: productId + '',
      variantId: variantId + '',
      lang: Shopify.localizationHVG.languageIso,
      country: Shopify.country,
    };
    if (scenario) {
      payload.scenario = scenario;
    }
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify(payload),
    });
    const result = await res.json();
    if (!result.success) {
      throw new Error(result.message);
    }
    if (typeof onSuccess === 'function') {
      onSuccess();
    }
  } catch (error) {
    if (typeof onFail === 'function') {
      onFail(error);
    }
  }
}
/**
 * Auto-enroll a logged-in customer for a variant that is currently out of stock.
 * No-op for guests.
 * @param {string|number} productId
 * @param {string|number} variantId
 * @param {string} handle
 */
async function tryAutoSubscribeBackInStock(productId, variantId, handle) {
  if (typeof customerId === 'undefined' || !customerId || typeof customerEmail === 'undefined' || !customerEmail) {
    return;
  }
  try {
    const res = await fetch(window._erj_.localePath('products/' + handle + '.js'));
    const product = await res.json();
    const variant = product.variants.find((v) => v.id == variantId);
    if (!variant || variant.available) return;

    subscribeBackToStock(
      productId,
      variantId,
      handle,
      customerEmail,
      () => console.log('[BIS] Auto-subscribed via wishlist:', handle, variantId),
      (err) => console.warn('[BIS] Auto-subscribe failed:', err),
      'wishlist'
    );
  } catch (err) {
    console.warn('[BIS] Could not check product availability:', err);
  }
}

// Cart drawer "apply discount code / gift card" input, migrated from the Vue cart app
// (formerly `discountCode` data + `applyDiscountCode`/`addGiftCardToCart` methods in
// assets/main.js). The single input tries a discount code first (cartDiscountCodesUpdate);
// if the cart already has a discount applied, or the code isn't a valid discount, it's
// retried as a gift card code (cartGiftCardCodesUpdate). Both mutations go through the
// existing Storefront GraphQL helper `window._erj_.fetchGraphQL`; refreshing the unified `state`
// after a successful mutation is delegated to `window.enrouteCart?.requestCartUpdate?.()`
// (the global Cart instance remains the single source of truth for that state).
class CartDiscountForm extends HTMLElement {
  constructor() {
    super();
    this._loading = false;
    // Mirrors the old `discountCode.inputDisabled` Vue flag: currently only ever reset to
    // false, never set true anywhere in this flow, but kept for behavioral parity.
    this._inputDisabled = false;
    // Mirrors `discountCodeApplyRes`: last discount-code warning code, used to decide the
    // gift-card fallback's own warning branch.
    this._applyResCode = '';
    this._warningTimer = null;
    this._loadingTimeout = null;
    this._onInput = this._onInput.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onApplyClick = this._onApplyClick.bind(this);
    this._onNotifClose = this._onNotifClose.bind(this);
    this._onCartUpdate = this._onCartUpdate.bind(this);
    this._onApplyGiftCardEvent = this._onApplyGiftCardEvent.bind(this);
    this._onRewardCodeClick = this._onRewardCodeClick.bind(this);
    this._onRewardToggle = this._onRewardToggle.bind(this);
    this._onOfferSuccess = (event) => this._showRewardSuccess(event.detail?.message);
    this._onOfferCancelled = (event) => {
      if (event.detail?.codes?.some((code) => code.toUpperCase() === this._input?.value?.toUpperCase())) this._resetInput();
    };
  }

  connectedCallback() {
    this._input = this.querySelector('[data-discount-input]');
    this._applyBtn = this.querySelector('[data-discount-apply]');
    this._applyText = this.querySelector('[data-discount-apply-text]');
    this._applyLoader = this.querySelector('[data-discount-apply-loader]');
    this._notifBank = this.querySelector('.cart-discount-notification');
    this._notifClose = this.querySelector('[data-discount-notif-close]');

    this._input?.addEventListener('input', this._onInput);
    this._input?.addEventListener('keydown', this._onKeydown);
    this._applyBtn?.addEventListener('click', this._onApplyClick);
    this._notifClose?.addEventListener('click', this._onNotifClose);
    window.addEventListener('enroute:cart-updated', this._onCartUpdate);
    // Bridges the gift-card-page "USE NOW" handoff, which used to call
    // `this.addGiftCardToCart()` directly from Vue's afterUpdateTrigger once the pending
    // sessionStorage code was read (assets/main.js).
    window.addEventListener('enroute:apply-gift-card', this._onApplyGiftCardEvent);
    window.addEventListener('enroute:cart-offer-cancelled', this._onOfferCancelled);
    window.addEventListener('enroute:cart-offer-success', this._onOfferSuccess);

    this._updateApplyButtonState();
    this._updateDisabled();
    this._renderRewardSuccess();
  }

  disconnectedCallback() {
    this._input?.removeEventListener('input', this._onInput);
    this._input?.removeEventListener('keydown', this._onKeydown);
    this._applyBtn?.removeEventListener('click', this._onApplyClick);
    this._notifClose?.removeEventListener('click', this._onNotifClose);
    this._rewardCodes?.removeEventListener('click', this._onRewardCodeClick);
    this._rewardCodes?.removeEventListener('toggle', this._onRewardToggle);
    this._finishRewardAnimation?.();
    this._closeRewardGiftPopup?.();
    this._rewardGiftRequestId = (this._rewardGiftRequestId || 0) + 1;
    window.removeEventListener('enroute:cart-updated', this._onCartUpdate);
    window.removeEventListener('enroute:apply-gift-card', this._onApplyGiftCardEvent);
    window.removeEventListener('enroute:cart-offer-cancelled', this._onOfferCancelled);
    window.removeEventListener('enroute:cart-offer-success', this._onOfferSuccess);
    clearTimeout(this._warningTimer);
    clearTimeout(this._loadingTimeout);
  }

  _onInput() {
    this._updateApplyButtonState();
  }

  _onKeydown(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this._applyDiscountCode();
  }

  _onApplyClick(event) {
    event.preventDefault();
    this._applyDiscountCode();
  }

  _onNotifClose() {
    this._closeNotif();
  }

  _onCartUpdate() {
    this._updateDisabled();
    const subtotal = Number(window.enrouteCart?.state?.original_total_price || 0);
    const appliedCodes = JSON.stringify(this._getAppliedOfferCodes());
    if (subtotal !== this._rewardSubtotal || appliedCodes !== this._rewardAppliedCodes ||
        JSON.stringify(this._rewardCurrency()) !== this._rewardCurrencySnapshot) {
      this._renderRewardCodes(window.accountInfo || { codeRecords: [] });
    }
  }

  _onApplyGiftCardEvent(event) {
    const code = event?.detail?.code;
    if (!code) return;
    if (this._input) this._input.value = code;
    this._addGiftCardToCart(code);
  }

  _offerText(key, values) {
    return window._erj_.cartOfferText(key, values);
  }

  _getAppliedOfferCodes() {
    return window._erj_.cartOffers.appliedCodes(window.enrouteCart?.state);
  }

  _getRewardOfferNames() {
    window._erj_ = window._erj_ || {};
    if (window._erj_.rewardOfferNames instanceof Map) return window._erj_.rewardOfferNames;
    window._erj_.rewardOfferNames = new Map();
    return window._erj_.rewardOfferNames;
  }

  _cacheRewardOfferName(code, name) {
    const key = String(code || '').trim().toUpperCase();
    const names = this._getRewardOfferNames();
    if (names.has(key)) return names.get(key);
    const label = String(name || '').trim();
    if (!key || !label || label.toUpperCase() === key) return code;
    names.set(key, label);
    return label;
  }

  _describeCartDiscount(code, cart = window.enrouteCart?.state) {
    const remembered = this._getRewardOfferNames().get(String(code || '').trim().toUpperCase());
    if (remembered) return remembered;
    const cartApplications = (cart?.cart_level_discount_applications || []).map((application) => ({ application }));
    const lineApplications = (cart?.items || []).flatMap((item) =>
      (item.line_level_discount_allocations || []).map((allocation) => ({
        application: allocation.discount_application,
        item,
        amount: allocation.amount,
      }))
    );
    const applications = [...cartApplications, ...lineApplications]
      .filter(({ application }) => application?.type === 'discount_code');
    const matching = applications.filter(({ application }) =>
      String(application.title || '').trim().toUpperCase() === code.toUpperCase()
    );
    const entry = matching[0] || (window._erj_.cartOffers.appliedCodes(cart).length === 1 ? applications[0] : null);
    const application = entry?.application;
    if (application) {
      const title = String(application.title || '').trim();
      const descriptiveTitle = title && title.toUpperCase() !== code.toUpperCase() &&
        /\b(buy|get|spend|free|over|minimum|off)\b|满|减/i.test(title);
      if (descriptiveTitle) return this._cacheRewardOfferName(code, title);
      if (application.target_type === 'shipping_line') return this._cacheRewardOfferName(code, this._offerText('free_shipping'));
      if (entry.item?.properties?._from === 'gift-selection' && Number(application.value) === 100) {
        return this._cacheRewardOfferName(code, this._offerText('free_product', { title: entry.item.product_title || entry.item.title || this._offerText('gift_name') }));
      }
      const value = Number(application.value);
      if (Number.isFinite(value) && value > 0) {
        if (application.value_type === 'percentage') return this._cacheRewardOfferName(code, this._offerText('percent_off', { value }));
        if (application.value_type === 'fixed_amount') return this._cacheRewardOfferName(code, this._offerText('amount_off', { amount: this._formatRewardAmount(value) }));
      }
      if (title && title.toUpperCase() !== code.toUpperCase()) return this._cacheRewardOfferName(code, title);
    }
    return code;
  }

  _formatRewardAmount(amount) {
    if (typeof window.Shopify?.formatMoney === 'function') {
      return window.Shopify.formatMoney(Math.round(amount * 100), window.Shopify.money_format).replace(/<[^>]*>/g, '');
    }
    return `$${amount}`;
  }

  _renderRewardSuccess() {
    const notice = this.parentElement?.querySelector('[data-reward-success]');
    if (!notice) return;
    const message = window._erj_?.rewardSuccessMessage || '';
    notice.hidden = !message;
    notice.querySelector('[data-reward-success-text]').textContent = message;
    notice.querySelector('[data-reward-success-close]').onclick = () => {
      clearTimeout(window._erj_.rewardSuccessTimer);
      delete window._erj_.rewardSuccessTimer;
      delete window._erj_.rewardSuccessMessage;
      notice.hidden = true;
    };
  }

  _showRewardSuccess(message) {
    if (!message) return;
    clearTimeout(window._erj_.rewardSuccessTimer);
    window._erj_.rewardSuccessMessage = message;
    document.querySelector('cart-discount-form')?._renderRewardSuccess();
    window._erj_.rewardSuccessTimer = setTimeout(() => {
      delete window._erj_.rewardSuccessTimer;
      delete window._erj_.rewardSuccessMessage;
      document.querySelector('cart-discount-form')?._renderRewardSuccess();
    }, 5000);
  }

  _rewardCurrency() {
    const currency = window.enrouteCart?.state?.currency || window.Shopify?.currency?.active || 'USD';
    return { currency, rate: currency === 'USD' ? 1 : window.Shopify?.currency?.rate };
  }

  _rewardOfferEligibility(record) {
    const minimumUsd = Math.max(0, Number(record.extra?.min_subtotal_usd) || 0);
    if (!minimumUsd) {
      return { eligible: true, reason: '' };
    }
    const { currency, rate } = this._rewardCurrency();
    if (!Number.isFinite(rate) || rate <= 0) {
      const result = { eligible: false, reason: this._offerText('retry_later') };
      return result;
    }
    // Compare in presentment cents; do not apply the display rounding used by promotions.
    const minimumCents = Math.ceil(minimumUsd * rate * 100 - 1e-8);
    const subtotalCents = Number(window.enrouteCart?.state?.original_total_price || 0);
    const remainingCents = Math.max(0, minimumCents - subtotalCents);
    if (!remainingCents) {
      return { eligible: true, reason: '' };
    }
    const amount = new Intl.NumberFormat(window.Shopify?.locale || 'en', {
      style: 'currency', currency,
    }).format(remainingCents / 100);
    const result = { eligible: false, reason: this._offerText('unlock_remaining', { amount }) };
    return result;
  }

  _rewardCodeTypeRank(record) {
    const type = record.extra?.type || record.type;
    const threshold = (Number(record.extra?.min_subtotal_usd) || 0) > 0;
    if (['free_product', 'product_variant', 'product_variants'].includes(type)) return 0;
    if (type === 'percent_discount') return threshold ? 3 : 1;
    if (type === 'fixed_discount') return threshold ? 4 : 2;
    return 5;
  }

  _sortRewardCodes(records) {
    const now = Date.now();
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    return records
      .filter((record) => {
        const expiry = record?.expiredAt ? new Date(record.expiredAt).getTime() : NaN;
        return record && String(record.code || '').trim() && (!Number.isFinite(expiry) || expiry > now);
      })
      .sort((a, b) => {
        const aEligible = this._rewardOfferEligibility(a).eligible;
        const bEligible = this._rewardOfferEligibility(b).eligible;
        if (aEligible !== bEligible) return aEligible ? -1 : 1;
        // Unavailable offers prioritize their type rather than their expiry bucket.
        if (!aEligible) return this._rewardCodeTypeRank(a) - this._rewardCodeTypeRank(b) ||
          (new Date(a.expiredAt).getTime() || Infinity) - (new Date(b.expiredAt).getTime() || Infinity);
        const aSoon = Boolean(a.expiredAt && new Date(a.expiredAt).getTime() - now <= oneWeek);
        const bSoon = Boolean(b.expiredAt && new Date(b.expiredAt).getTime() - now <= oneWeek);
        if (aSoon !== bSoon) return aSoon ? -1 : 1;
        return this._rewardCodeTypeRank(a) - this._rewardCodeTypeRank(b) || (new Date(a.expiredAt).getTime() || Infinity) - (new Date(b.expiredAt).getTime() || Infinity);
      });
  }

  _escapeRewardHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[character]);
  }

  _rewardCardName(record) {
    const key = String(record.code || '').toUpperCase();
    const name = record._cartOnly
      ? this._getRewardOfferNames().get(key) || record.codeName || record.code
      : record.codeName || record.code;
    const birthday = /^Birthday Gift\s*(?:\(\s*(\d+(?:\.\d+)?)\s*%\s*Off\s*\))?$/i.exec(name || '');
    if (birthday) {
      const title = this._offerText('birthday_reward');
      return birthday[1] ? `${title} (${this._offerText('percent_off', { value: birthday[1] })})` : title;
    }
    if (/^Referee Order Completed Reward$/i.test(name || '')) return this._offerText('referee_reward');
    if (/^Referrer Order Completed Reward$/i.test(name || '')) return this._offerText('referrer_reward');
    const percent = /^(\d+(?:\.\d+)?)\s*%\s*off$/i.exec(name || '');
    if (percent) return this._offerText('percent_off', { value: percent[1] });
    const amount = /^(\$\d+(?:\.\d+)?)\s*off$/i.exec(name || '');
    if (amount) return this._offerText('amount_off', { amount: amount[1] });
    if (/^Free Shipping$/i.test(name || '')) return this._offerText('free_shipping');
    return name;
  }

  _renderRewardCard(record, { key, signature, isGift, eligible, reason, appliedCodes, rewardCodes }) {
    const html = (value) => this._escapeRewardHtml(value);
    const name = this._rewardCardName(record);
    const expiry = record.expiredAt ? new Date(record.expiredAt).getTime() : NaN;
    const expiryText = Number.isFinite(expiry) ? this._offerText('expires', {
      date: new Date(expiry).toLocaleDateString(window.Shopify?.locale || 'en', { month: 'short', day: 'numeric', year: 'numeric' }),
    }) : '';
    const applied = appliedCodes.some((code) => code.toUpperCase() === String(record.code).toUpperCase());
    const switching = appliedCodes.length > 0 && !applied;
    const label = this._offerText(applied ? 'applied_offer_label' : !eligible ? 'unavailable_offer_label' : switching ? 'switch_offer_label' : 'apply_offer_label', { name });
    const applyControl = `
        <span class="cart-reward-code__apply" data-reward-apply>
          <span data-reward-apply-text>${html(this._offerText(applied ? 'applied' : 'apply'))}</span>
          <span class="loader-dot-hvg-white" data-offer-loader aria-hidden="true" hidden><i></i><i></i><i></i></span>
        </span>`;
    const template = document.createElement('template');
    template.innerHTML = `
      <article class="cart-reward-code${isGift ? ' cart-reward-code--gift' : ''}${applied ? ' is-selected' : ''}${!eligible ? ' is-unavailable' : ''}"
        data-reward-card-code="${html(key)}" data-reward-card-signature="${html(signature)}">
        <div class="cart-reward-code__image" aria-hidden="true">
          <img src="${html(record.thumbnailUrl || 'https://cdn.shopify.com/s/files/1/1782/4093/files/1_8c5f5ebe-0d50-4057-b697-ef0f3fcc9fea.png')}" alt="" loading="lazy">
        </div>
        <div class="cart-reward-code__copy">
          <div class="cart-reward-code__header">
            <div class="cart-reward-code__name">${html(name)}</div>
            <div class="cart-reward-code__expiry">${html(expiryText)}</div>
          </div>
          <div class="cart-reward-code__details">
            <div class="cart-reward-code__code" title="${html(record.code)}">${html(record.code)}</div>
          </div>
          <span class="cart-reward-code__reason" role="status">${html(reason)}</span>
          <span class="cart-reward-code__status" role="status">${html(applied || !eligible ? '' : isGift ? this._offerText('loading_gifts') : '')}</span>
          ${isGift ? `
            <div class="cart-reward-code__choice gift-controls">
              <div class="gift-variant-switcher hvg-cart-variant-switcher" data-reward-gift-switcher="${html(record.code)}">
                <button type="button" class="hvg-cart-variant-switcher-info gift-variant-toggle" data-reward-gift-toggle
                  aria-label="${html(this._offerText('choose_gift_label'))}" aria-expanded="false" ${!eligible ? 'disabled' : ''}>
                  <span class="hvg-cart-variant-switcher-info-items" data-reward-gift-selected>${html(this._offerText('choose_gift_size'))}</span>
                  <span class="hvg-cart-variant-switcher-info-symbol-img" aria-hidden="true">
                    <svg width="10" height="5" viewBox="0 0 128 128" preserveAspectRatio="none" fill="none" aria-hidden="true" focusable="false"><path d="M6 35 64 92 122 35" stroke="#020202" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </span>
                </button>
                <anchored-popup anchor="[data-reward-gift-toggle]" fixed min-width="180" max-height="240"
                  class="hvg-cart-variant-switcher-popup gift-variant-popup" data-reward-gift-options hidden></anchored-popup>
              </div>
              ${applyControl}
            </div>` : applyControl}
        </div>
        <button type="button" class="cart-reward-code__select" aria-label="${html(label)}" aria-pressed="${applied}"
          data-reward-code="${html(record.code)}" ${!eligible ? 'disabled' : ''} ${switching ? 'data-switch-offer' : ''}></button>
      </article>`;
    const row = template.content.firstElementChild;
    row._rewardRecord = record;
    return row;
  }

  _renderRewardCodes(data, resolvedGiftRules) {
    const rewardCodes = this.parentElement?.querySelector('[data-cart-reward-codes]');
    const rewardCodeGroups = rewardCodes?.querySelector('[data-reward-code-groups]');
    if (!rewardCodes || !rewardCodeGroups) return false;
    this._rewardData = data;
    this._renderRewardSuccess();
    this._finishRewardAnimation?.();
    if (this._rewardCodes !== rewardCodes) {
      this._rewardCodes?.removeEventListener('click', this._onRewardCodeClick);
      this._rewardCodes?.removeEventListener('toggle', this._onRewardToggle);
      this._rewardCodes = rewardCodes;
      // Section swaps recreate <details>; keep the user's choice for this page.
      window._erj_ = window._erj_ || {};
      rewardCodes.open = window._erj_.cartRewardCodesExpanded ?? false;
      rewardCodes.addEventListener('click', this._onRewardCodeClick);
      rewardCodes.addEventListener('toggle', this._onRewardToggle);
      this._onRewardToggle();
    }
    try {
      const accountRecords = Array.isArray(data?.codeRecords) ? data.codeRecords : [];
      const knownCodes = new Set(accountRecords.filter((record) => {
        const expiry = record?.expiredAt ? new Date(record.expiredAt).getTime() : NaN;
        return String(record?.code || '').trim() && (!Number.isFinite(expiry) || expiry > Date.now());
      }).map((record) => String(record.code).trim().toUpperCase()));
      // Cart-only and historical offers cannot make this module visible.
      if (typeof customerId === 'undefined' || !customerId || knownCodes.size === 0) {
        rewardCodes.hidden = true;
        this._closeRewardGiftPopup?.();
        this._rewardGiftRequestId = (this._rewardGiftRequestId || 0) + 1;
        rewardCodeGroups.replaceChildren();
        rewardCodes.querySelector('[data-reward-code-count]').textContent = '(0)';
        return true;
      }
      const appliedCodes = this._getAppliedOfferCodes();
      window._erj_ = window._erj_ || {};
      const history = window._erj_.rewardOfferHistory ||= new Map();
      for (const code of appliedCodes) {
        if (!knownCodes.has(code.toUpperCase())) {
          const key = code.toUpperCase();
          const previous = history.get(key);
          const description = this._describeCartDiscount(code);
          const stableName = this._getRewardOfferNames().get(key) ||
            (previous?.codeName && !['Current offer', 'Previous offer'].includes(previous.codeName) ? previous.codeName : null) ||
            description;
          if (!previous || previous.codeName !== stableName) {
            history.set(key, { ...previous, code, codeName: stableName, _cartOnly: true });
          }
        }
      }
      const records = [...accountRecords, ...[...history.values()].filter((record) => !knownCodes.has(record.code.toUpperCase()))];
      this._rewardSubtotal = Number(window.enrouteCart?.state?.original_total_price || 0);
      this._rewardAppliedCodes = JSON.stringify(appliedCodes);
      this._rewardCurrencySnapshot = JSON.stringify(this._rewardCurrency());
      const sorted = this._sortRewardCodes(records);
      const existingRows = new Map([...rewardCodeGroups.children].map((row) => [row.dataset.rewardCardCode, row]));
      const pendingGifts = [];
      rewardCodes.querySelector('[data-reward-code-count]').textContent = `(${sorted.length})`;
      sorted.forEach((record, index) => {
        const { eligible, reason } = this._rewardOfferEligibility(record);
        const type = record.extra?.type || record.type;
        const isGift = ['free_product', 'product_variant', 'product_variants'].includes(type);
        const key = String(record.code).toUpperCase();
        const signature = JSON.stringify(record);
        const existing = existingRows.get(key);
        if (existing?.dataset.rewardCardSignature === signature) {
          this._syncRewardCard(existing, record, appliedCodes);
          if (rewardCodeGroups.children[index] !== existing) rewardCodeGroups.insertBefore(existing, rewardCodeGroups.children[index] || null);
          existingRows.delete(key);
          if ((isGift && !existing._syncRewardGiftFromCart) || (record._cartOnly && !existing._rewardGiftLookupComplete)) pendingGifts.push(record);
          return;
        }
        existing?._giftPicker?.destroy();
        existing?.remove();
        existingRows.delete(key);
        const row = this._renderRewardCard(record, { key, signature, isGift, eligible, reason, appliedCodes, rewardCodes });
        if (isGift || record._cartOnly) pendingGifts.push(record);
        rewardCodeGroups.insertBefore(row, rewardCodeGroups.children[index] || null);
      });
      existingRows.forEach((row) => { row._giftPicker?.destroy(); row.remove(); });
      rewardCodes.hidden = sorted.length === 0;
      if (!pendingGifts.length) return true;
      const giftRequestId = this._rewardGiftRequestId = (this._rewardGiftRequestId || 0) + 1;
      this._rewardGiftOptionsReady = this._loadRewardGiftOptions(pendingGifts, rewardCodes, giftRequestId, resolvedGiftRules);
      this._rewardGiftOptionsReady.then(() => {
        if (!rewardCodes.isConnected || giftRequestId !== this._rewardGiftRequestId) return;
        rewardCodes.querySelectorAll('.cart-reward-code--gift').forEach((row) => {
          const button = row.querySelector('[data-reward-code]');
          if (button.disabled || button.getAttribute('aria-pressed') === 'true') return;
          const status = row.querySelector('.cart-reward-code__status');
          if (status) status.textContent = button.dataset.rewardVariantId ? '' : this._offerText('gift_options_unavailable');
        });
      });
      return true;
    } catch (error) {
      console.warn('Unable to load reward codes for cart', error);
      return false;
    }
  }

  _syncRewardCard(row, record, appliedCodes) {
    row._rewardRecord = record;
    const applied = appliedCodes.some((code) => code.toUpperCase() === String(record.code).toUpperCase());
    const { eligible, reason } = this._rewardOfferEligibility(record);
    const button = row.querySelector('[data-reward-code]');
    const soldOut = button.dataset.rewardSoldOut === 'true';
    row.classList.toggle('is-selected', applied);
    row.classList.toggle('is-unavailable', soldOut || !eligible);
    row.querySelector('.cart-reward-code__reason').textContent = reason;
    const applyText = row.querySelector('[data-reward-apply-text]');
    if (applyText) applyText.textContent = this._offerText(applied ? 'applied' : 'apply');
    button.setAttribute('aria-pressed', String(applied));
    button.setAttribute('aria-label', this._offerText(applied ? 'applied_offer_label' : !eligible ? 'unavailable_offer_label' : appliedCodes.length ? 'switch_offer_label' : 'apply_offer_label', { name: row.querySelector('.cart-reward-code__name').textContent }));
    button.disabled = (!eligible || soldOut || button.dataset.loading === 'true');
    button.toggleAttribute('data-switch-offer', appliedCodes.length > 0 && !applied);
    const toggle = row.querySelector('[data-reward-gift-toggle]');
    if (toggle) toggle.disabled = !eligible || soldOut;
    const status = row.querySelector('.cart-reward-code__status');
    if (applied && !soldOut) status.textContent = '';
    else if (soldOut) status.textContent = this._offerText('sold_out');
    else if (!eligible) status.textContent = '';
    else if (!row.classList.contains('cart-reward-code--gift') || button.dataset.rewardVariantId) status.textContent = '';
    row._syncRewardGiftFromCart?.();
  }

  _onRewardToggle() {
    const label = this._rewardCodes?.querySelector('[data-reward-toggle-text]');
    const expanded = this._rewardExpanded ?? this._rewardCodes?.open;
    if (label) label.textContent = expanded ? this._offerText('hide_offers') : this._offerText('view_offers');
    if (!expanded) this._closeRewardGiftPopup?.();
  }

  _animateRewardToggle() {
    const root = this._rewardCodes;
    const list = root?.querySelector('[data-reward-code-groups]');
    if (!root || !list) return;
    const expanded = !(this._rewardExpanded ?? root.open);
    // Capture the current frame before cancelling so rapid clicks reverse smoothly.
    const startHeight = root.getBoundingClientRect().height;
    const startOpacity = root.open ? getComputedStyle(list).opacity : '0';
    const startTransform = root.open ? getComputedStyle(list).transform : 'translateY(-6px)';
    this._finishRewardAnimation?.();
    window._erj_ = window._erj_ || {};
    window._erj_.cartRewardCodesExpanded = expanded;
    if (!root.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      root.open = expanded;
      this._onRewardToggle();
      return;
    }
    this._rewardExpanded = expanded;
    root.classList.toggle('is-expanding', expanded);
    root.classList.toggle('is-collapsing', !expanded);
    root.open = false;
    const closedHeight = root.getBoundingClientRect().height;
    root.open = true;
    const endHeight = expanded ? root.getBoundingClientRect().height : closedHeight;
    this._onRewardToggle();
    const timing = { duration: expanded ? 320 : 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' };
    // Clip the cards without creating a scroll container that repositions the sticky header.
    const heightAnimation = root.animate([
      { height: `${startHeight}px`, overflow: 'clip' },
      { height: `${endHeight}px`, overflow: 'clip' },
    ], timing);
    const contentAnimation = list.animate([
      { opacity: startOpacity, transform: startTransform },
      { opacity: expanded ? 1 : 0, transform: expanded ? 'translateY(0)' : 'translateY(-6px)' },
    ], timing);
    const finish = () => {
      heightAnimation.onfinish = null;
      root.open = expanded;
      heightAnimation.cancel();
      contentAnimation.cancel();
      root.classList.remove('is-expanding', 'is-collapsing');
      this._rewardExpanded = undefined;
      this._finishRewardAnimation = null;
      this._onRewardToggle();
    };
    this._finishRewardAnimation = finish;
    heightAnimation.onfinish = finish;
  }

  async _loadRewardGiftOptions(records, root, requestId, resolvedRules) {
    if (!records.length) return;
    const codes = records.map((record) => String(record.code).trim().toUpperCase());
    try {
      const rules = resolvedRules || await window._erj_.cartOffers.lookup(codes);
      if (!root.isConnected || requestId !== this._rewardGiftRequestId) return;
      let reclassified = false;
      for (const record of records) {
        if (!record._cartOnly) continue;
        const key = String(record.code).trim().toUpperCase();
        const rule = rules.find((item) => item.code === key);
        if (rule && record.extra?.type !== 'free_product') {
          record.extra = { ...record.extra, type: 'free_product' };
          reclassified = true;
        }
        const row = [...root.querySelectorAll('.cart-reward-code')]
          .find((item) => item.dataset.rewardCardCode === key);
        if (row) row._rewardGiftLookupComplete = true;
      }
      if (reclassified) {
        // Rebuild gift controls using the same normalized rules, without a second request.
        this._renderRewardCodes(this._rewardData || window.accountInfo || { codeRecords: [] }, rules);
        await this._rewardGiftOptionsReady;
        return;
      }
      await Promise.all(rules.map(async (rule) => {
        const record = records.find((item) => String(item.code).trim().toUpperCase() === rule.code);
        const products = record?.extra?.products || [];
        await Promise.all(rule.products.map(async (product) => {
          const source = products.find((item) => String(item.product_id).split('/').pop() === String(product.productId).split('/').pop());
          const handle = product.handle || source?.handle || (products.length === 1 && rule.products.length === 1 ? products[0].handle : '');
          if (!handle) return;
          try {
            const response = await fetch(window._erj_.localePath(`products/${encodeURIComponent(handle)}.js`));
            if (!response.ok) return;
            const localized = await response.json();
            if (localized.title) product.title = localized.title;
            for (const variant of product.variants) {
              const translated = localized.variants?.find((item) => String(item.id) === window._erj_.cartOffers.variantId(variant.id));
              if (translated?.title) variant.title = translated.title;
            }
          } catch (error) {
            console.warn('Unable to localize reward gift', error);
          }
        }));
      }));
      if (!root.isConnected || requestId !== this._rewardGiftRequestId) return;
      for (const rule of rules) {
        const switcher = [...root.querySelectorAll('[data-reward-gift-switcher]')]
          .find((item) => item.dataset.rewardGiftSwitcher.toUpperCase() === rule.code);
        if (switcher) this._setupRewardGiftPicker(switcher, rule);
      }
    } catch (error) {
      console.warn('Unable to load reward gift options', error);
    }
  }

  _closeRewardGiftPopup() {
    this._rewardCodes?.querySelectorAll('.cart-reward-code').forEach((row) => row._giftPicker?.close());
  }

  _setupRewardGiftPicker(switcher, rule) {
    const offers = window._erj_.cartOffers;
    const owner = () => document.querySelector('cart-discount-form') || this;
    const toggle = switcher.querySelector('[data-reward-gift-toggle]');
    const popup = switcher.querySelector('[data-reward-gift-options]');
    const row = switcher.closest('.cart-reward-code');
    const confirm = row?.querySelector('[data-reward-code]');
    if (!toggle || !popup || !confirm) return;
    row._giftPicker?.destroy();
    const current = row.classList.contains('is-selected') && offers.findGift(rule, window.enrouteCart?.state, true);
    const picker = row._giftPicker = offers.createPicker({
      rule, switcher, toggle, popup,
      label: switcher.querySelector('[data-reward-gift-selected]'),
      image: row.querySelector('.cart-reward-code__image'),
      selectedId: current && (current.variant_id || current.id),
      isBusy: () => offers.busy || owner()._loading,
      onChange: (product, variant) => {
        row.querySelector('.cart-reward-code__name').textContent = product.title;
        const applied = row.classList.contains('is-selected');
        confirm.setAttribute('aria-label', this._offerText(applied ? 'applied_offer_label' : 'apply_offer_label', { name: product.title }));
        confirm.dataset.rewardVariantId = variant.id;
        confirm.disabled = !owner()._rewardOfferEligibility(row._rewardRecord || {}).eligible;
      },
      onSelect: async (_product, variant) => {
        if (!row.classList.contains('is-selected')) return;
        toggle.disabled = true;
        row.classList.add('is-loading');
        try {
          await offers.changeVariant(rule.code, confirm.dataset.rewardVariantId, variant.id);
          owner()._showRewardSuccess(this._offerText('gift_variant_updated'));
        } finally {
          toggle.disabled = false;
          row.classList.remove('is-loading');
        }
      },
    });
    row._syncRewardGiftFromCart = () => {
      if (!row.classList.contains('is-selected')) return;
      const item = offers.findGift(rule, window.enrouteCart?.state, true);
      const id = item && (item.variant_id || item.id);
      if (id && offers.variantId(id) !== offers.variantId(picker.selected?.variant.id)) picker.select(id);
    };
    confirm.dataset.rewardSoldOut = String(!picker.available);
    toggle.disabled = !picker.available || !owner()._rewardOfferEligibility(row._rewardRecord || {}).eligible;
    if (!picker.available) {
      confirm.disabled = true;
      row.classList.add('is-unavailable');
      row.querySelector('.cart-reward-code__status').textContent = this._offerText('sold_out');
      if (!picker.selected) switcher.querySelector('[data-reward-gift-selected]').textContent = this._offerText('sold_out');
    }
  }

  _setRewardButtonLoading(button, loading) {
    if (!button) return;
    const row = button.closest('.cart-reward-code');
    button.disabled = loading || button.dataset.rewardSoldOut === 'true' ||
      (row?._rewardRecord && !this._rewardOfferEligibility(row._rewardRecord).eligible);
    button.dataset.loading = String(loading);
    button.setAttribute('aria-busy', String(loading));
    row?.classList.toggle('is-loading', loading);
    const applyText = row?.querySelector('[data-reward-apply-text]');
    if (applyText) applyText.hidden = loading;
    const loader = row?.querySelector('[data-offer-loader]');
    if (loader) loader.hidden = !loading;
    if (!loading) delete button.dataset.loading;
  }

  async _onRewardCodeClick(event) {
    if (event.target.closest('.cart-reward-codes__head')) {
      event.preventDefault();
      this._animateRewardToggle();
      return;
    }
    let button = event.target.closest('[data-reward-code]');
    if (!button) {
      // Text/images can sit above the card button in the theme's stacking contexts.
      // Keep variant controls independent, and route the remaining card surface here.
      if (event.target.closest('button, input, select, a, [data-reward-gift-switcher]')) return;
      // fallback to first [data-reward-code]
      button = event.target.closest('.cart-reward-code')?.querySelector('[data-reward-code]');
    }
    if (!button || button.disabled || !this._input || this._loading || window._erj_.cartOffers?.busy) return;
    if (button.getAttribute?.('aria-pressed') === 'true') return;

    this._setRewardButtonLoading(button, true);
    try {
      const record = button.closest('.cart-reward-code')?._rewardRecord;
      if (record?._cartOnly) {
        await this._rewardGiftOptionsReady;
        if (!this._rewardCodes?.isConnected) return;
        if (!button.closest('.cart-reward-code')?._rewardGiftLookupComplete) {
          this._rewardGiftOptionsReady = this._loadRewardGiftOptions([record], this._rewardCodes, this._rewardGiftRequestId);
          await this._rewardGiftOptionsReady;
        }
        // Rule lookup can replace a generic historical card with a gift card.
        button = [...this._rewardCodes.querySelectorAll('[data-reward-code]')]
          .find((item) => item.dataset.rewardCode === record.code);
        if (!button || button.dataset.rewardSoldOut === 'true') return;
        if (!button.closest('.cart-reward-code')?._rewardGiftLookupComplete) throw new Error(this._offerText('gift_load_failed'));
        this._setRewardButtonLoading(button, true);
      }
      const isGift = Boolean(button.closest('.cart-reward-code--gift'));
      if (isGift && !button.dataset.rewardVariantId) {
        const status = button.closest('.cart-reward-code').querySelector('.cart-reward-code__status');
        await this._rewardGiftOptionsReady;
        if (!button.dataset.rewardVariantId && this._rewardCodes?.isConnected) {
          this._rewardGiftOptionsReady = this._loadRewardGiftOptions(
            [{ code: button.dataset.rewardCode }], this._rewardCodes, this._rewardGiftRequestId
          );
          await this._rewardGiftOptionsReady;
        }
        if (!button.isConnected) return;
        if (!button.dataset.rewardVariantId) {
          if (status) status.textContent = this._offerText('gift_load_failed');
          return;
        }
        if (status) status.textContent = '';
      }
      const form = document.querySelector('cart-discount-form');
      if (!form?._input || form._loading) return;
      await form._applyRewardOffer(button.dataset.rewardCode || '', isGift ? button.dataset.rewardVariantId : null);
    } catch (error) {
      window._erj_?.cartSections?.showError?.(error.message);
    } finally {
      this._setRewardButtonLoading(button, false);
    }
  }

  async _applyRewardOffer(code, variantId, options) {
    this._setLoading(true);
    try {
      const result = await window._erj_.cartOffers.apply(code, variantId, options);
      const previousCodes = result.previousCodes;
      const previousDescriptions = new Map(previousCodes.map((oldCode) =>
        [oldCode.toUpperCase(), this._describeCartDiscount(oldCode, result.previousCart)]
      ));
      window._erj_.rewardOfferHistory ||= new Map();
      const available = new Set((window.accountInfo?.codeRecords || []).filter((record) => {
        const expiry = record?.expiredAt ? new Date(record.expiredAt).getTime() : NaN;
        return String(record?.code || '').trim() && (!Number.isFinite(expiry) || expiry > Date.now());
      }).map((record) => String(record.code).toUpperCase()));
      for (const oldCode of previousCodes) {
        if (!available.has(oldCode.toUpperCase())) {
          const key = oldCode.toUpperCase();
          const previous = window._erj_.rewardOfferHistory.get(key);
          window._erj_.rewardOfferHistory.set(key, {
            ...previous,
            code: oldCode,
            codeName: this._getRewardOfferNames().get(key) ||
              (previous?.codeName && !['Current offer', 'Previous offer'].includes(previous.codeName) ? previous.codeName : null) ||
              previousDescriptions.get(key) || oldCode,
            _cartOnly: true,
          });
        }
      }
      window._erj_.renderCartRewardCodes?.(window.accountInfo || { codeRecords: [] });
      if (variantId) {
        this._showRewardSuccess(result.reused
          ? this._offerText('offer_applied_to_item', { title: result.item.product_title || result.item.title || this._offerText('item') })
          : this._offerText('gift_added_offer_applied'));
      } else {
        this._showRewardSuccess(this._offerText('applied_offer_label', { name: this._describeCartDiscount(code) }));
      }
      return true;
    } catch (error) {
      const form = document.querySelector('cart-discount-form') || this;
      form._showWarning(error.warningCode || 'OTHER_REASON');
      window._erj_.cartSections?.showError?.(error.message);
      return false;
    } finally {
      this._setLoading(false);
    }
  }

  _updateApplyButtonState() {
    if (!this._applyBtn || !this._input) return;
    this._applyBtn.classList.toggle('hvgapplybuttongrey', this._input.value.length < 4);
  }

  _updateDisabled() {
    const hasDiscount = Boolean(
      window.enrouteCart?.state &&
        ((window.enrouteCart.state.cart_level_discount_applications &&
          window.enrouteCart?.state?.cart_level_discount_applications?.length > 0) ||
          (window.enrouteCart?.state?.product_level_discount_applications &&
            window.enrouteCart.state.product_level_discount_applications.length > 0))
    );
    const hasGiftCard = Boolean(window.enrouteCart?.state?.appliedGiftCards?.length > 0);
    if (this._input) this._input.disabled = this._inputDisabled || (hasDiscount && hasGiftCard);
  }

  _setLoading(loading) {
    this._loading = loading;
    if (this._applyText) this._applyText.hidden = loading;
    if (this._applyLoader) this._applyLoader.hidden = !loading;
    clearTimeout(this._loadingTimeout);
    // Safety net: if no GraphQL callback ever fires (request hung or dropped),
    // restore the button so the loader can't spin forever. Input is kept so the
    // customer can retry without retyping the code.
    if (loading) {
      this._loadingTimeout = setTimeout(() => {
        if (!this._loading) return;
        this._setLoading(false);
        this._showWarning('OTHER_REASON');
      }, 15000);
    }
  }

  // Mirrors assets/main.js `applyDiscountCode`: if the cart has no discount applied yet,
  // try the code as a discount code; otherwise (or on a not-found/no-warning result) fall
  // through to trying it as a gift card code.
  _applyDiscountCode(successMessage) {
    const code = this._input ? this._input.value : '';
    if (!window.enrouteCart?.state || code.length <= 3) return;
    if (this._loading || window._erj_.cartOffers?.busy) return;

    window._erj_?.nativeGiftSelection?.clear();
    this._setLoading(true);

    if (
      window.enrouteCart.state.cart_level_discount_applications?.length === 0 &&
      window.enrouteCart.state.product_level_discount_applications?.length === 0
    ) {
      const cartId = `gid://shopify/Cart/${window.enrouteCart.state.token}`;
      const query = `
      mutation {
        cartDiscountCodesUpdate(cartId: "${cartId}", discountCodes: ["${code}"]) {
          cart {
            id
            discountCodes {
              applicable
              code
            }
          }
          warnings {
            code
            message
          }
          userErrors {
            field
            message
          }
        }
      }`;
      window._erj_.fetchGraphQL(
        query,
        (res) => {
          if (
            res.data.cartDiscountCodesUpdate?.cart?.discountCodes?.some(
              (i) => i?.code?.toLowerCase() === code.toLowerCase() && i?.applicable
            )
          ) {
            this._showRewardSuccess(successMessage);
            this._resetInput();
            window.enrouteCart?.requestCartUpdate?.();
          } else if (
            typeof window._erj_?.nativeGiftSelection?.update === 'function' &&
            window._erj_.nativeGiftSelection.intercepts(code) &&
            res.data.cartDiscountCodesUpdate?.warnings?.some(
              (warning) => warning.code === 'DISCOUNT_NO_ENTITLED_LINE_ITEMS'
            )
          ) {
            // Only intercept this warning when the enabled gift module can handle it.
            // Do not retry the discount as a gift card when its products are missing.
            this._closeNotif();
            // Stay loading through the rule lookup and any automatic single-variant
            // claim; the apply button is the customer's only progress cue meanwhile.
            const settled = window._erj_?.nativeGiftSelection?.update([{ code }]);
            if (typeof settled?.then !== 'function') {
              this._setLoading(false);
            } else {
              settled.then((result) => {
                this._setLoading(false);
                if (!result?.autoAdded?.length) return;
                // The automatic claim refreshes the sections, replacing this form;
                // reset and notify through the live one.
                const form = (typeof document !== 'undefined' && document.querySelector('cart-discount-form')) || this;
                form._resetInput();
                form._showSuccessNotif();
              }).catch(() => this._setLoading(false));
            }
            const scrollEl = typeof document === 'undefined' ? null : document.getElementById('hvg-cart-scrollable-container');
            if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
          } else if (
            !res.data.cartDiscountCodesUpdate?.warnings?.[0]?.code ||
            res.data.cartDiscountCodesUpdate?.warnings?.[0]?.code === 'DISCOUNT_NOT_FOUND'
          ) {
            this._applyResCode = res.data.cartDiscountCodesUpdate?.warnings?.[0]?.code;
            this._addGiftCardToCart(code);
          } else {
            if (res.data.cartDiscountCodesUpdate?.warnings?.[0]?.code) {
              this._showWarning(res.data.cartDiscountCodesUpdate.warnings[0].code);
            }
            this._resetInput();
          }
        },
        () => this._resetInput(),
        { version: '2025-07' }
      );
    } else {
      this._addGiftCardToCart(code);
    }
  }

  // Mirrors assets/main.js `addGiftCardToCart`. `removeDiscountCodeFromCart` and
  // `requestCartUpdate` stay on the global Cart instance: the former is still shared with the
  // applied-discount pills (cart-popup.liquid's cart-level/product-level discount list),
  // which use the same controller.
  _addGiftCardToCart(code) {
    if (!window.enrouteCart) return;

    if (!window.enrouteCart.state?.appliedGiftCards?.length) {
      const cartId = `gid://shopify/Cart/${window.enrouteCart.state.token}`;
      const query = `
       mutation {
        cartGiftCardCodesUpdate(cartId:  "${cartId}", giftCardCodes: ["${code}"]) {
          cart{
            appliedGiftCards {
              lastCharacters
            }
          }
          userErrors {
            field
            message
          }
          warnings {
            code
            message
          }
        }
      }`;

      window._erj_.fetchGraphQL(
        query,
        (res) => {
          const appliedGiftCards = res.data?.cartGiftCardCodesUpdate?.cart?.appliedGiftCards || [];
          if (res && appliedGiftCards.some((el) => code.toLowerCase().endsWith(el.lastCharacters.toLowerCase()))) {
            const promoToDelete = code.toLowerCase();
            window.enrouteCart?.removeDiscountCodeFromCart?.(promoToDelete);
            window.enrouteCart?.requestCartUpdate?.();
          } else if (
            this._applyResCode === 'DISCOUNT_NOT_FOUND' &&
            res.data.cartGiftCardCodesUpdate?.warnings?.[0]?.code === 'DISCOUNT_NOT_FOUND'
          ) {
            this._showWarning('DISCOUNT_NOT_FOUND');
          } else {
            this._showWarning(res.data.cartGiftCardCodesUpdate?.warnings?.[0]?.code || 'OTHER_REASON');
          }
          this._resetInput();
        },
        () => this._resetInput(),
        { version: '2025-07' }
      );
    } else if (this._applyResCode === 'DISCOUNT_NOT_FOUND') {
      this._showWarning('DISCOUNT_NOT_FOUND');
      this._resetInput();
    } else {
      this._showWarning('OTHER_REASON');
      this._resetInput();
    }
  }

  _resetInput() {
    this._setLoading(false);
    if (this._input) this._input.value = '';
    this._inputDisabled = false;
    this._updateApplyButtonState();
    this._updateDisabled();
    const scrollEl = document.getElementById('hvg-cart-scrollable-container');
    if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
  }

  _showWarning(code) {
    if (!this._notifBank) return;
    const el = this._notifBank.querySelector(`#${code}`);
    if (!el) return;
    this._notifBank.classList.remove('is-success');
    this._notifBank.querySelectorAll('.cart-discount-notification-notif').forEach((notif) => {
      notif.style.display = 'none';
    });
    el.style.display = '';
    this._notifBank.style.display = '';
    clearTimeout(this._warningTimer);
    this._warningTimer = setTimeout(() => this._closeNotif(), 5000);
  }

  _showSuccessNotif() {
    if (!this._notifBank) return;
    const el = this._notifBank.querySelector('#GIFT_AUTO_ADDED_SUCCESS');
    if (!el) return;
    this._closeNotif();
    el.style.display = '';
    this._notifBank.classList.add('is-success');
    this._notifBank.style.display = '';
    clearTimeout(this._warningTimer);
    this._warningTimer = setTimeout(() => this._closeNotif(), 5000);
  }

  _closeNotif() {
    if (!this._notifBank) return;
    this._notifBank.style.display = 'none';
    this._notifBank.classList.remove('is-success');
    this._notifBank.querySelectorAll('.cart-discount-notification-notif').forEach((el) => {
      el.style.display = 'none';
    });
  }
}
if (!customElements.get('cart-discount-form')) customElements.define('cart-discount-form', CartDiscountForm);

window._erj_ = window._erj_ || {};
window._erj_.renderCartRewardCodes = (accountInfo) => {
  const form = document.querySelector('cart-discount-form');
  if (!form) return false;
  return form._renderRewardCodes(accountInfo);
};
window._erj_.renderCartRewardCodes(window.accountInfo || { codeRecords: [] });

/**
 * Shared Section Rendering API plumbing for the cart drawer.
 *
 * The drawer's line-item list, totals block and upsell module are
 * server-rendered (snippets/cart-items.liquid, snippets/cart-totals.liquid,
 * snippets/cart-upsell.liquid) and are also exposed as standalone sections
 * (sections/cart-items.liquid, sections/cart-totals.liquid,
 * sections/cart-upsell.liquid)
 * purely so the cart Ajax endpoints can return fresh HTML for them via the
 * `sections` parameter. Those sections are never placed on a page with
 * {% section %} — Shopify renders any file under sections/ on request, so they
 * only need to exist.
 *
 * Drawer mutations use `mutateCart()` here, which asks
 * for the fragments in the same round trip and swaps them in. `refreshSections()`
 * covers the cases where the cart changed by some other route (the Storefront
 * GraphQL discount/gift-card mutations, for example) and only a re-render is
 * needed.
 */
(function () {
  if (window._erj_ && window._erj_.cartSections) return;

  /** Section files under sections/, keyed by the custom element they render into. */
  const SECTIONS = {
    'cart-items': 'cart-items',
    'cart-totals': 'cart-totals',
    'cart-upsell': 'cart-upsell',
    // Account page "Shopping Cart" module; applySections skips it on pages
    // where the host is absent.
    'account-cart-items': 'account-cart-items',
  };
  const SECTION_IDS = Object.keys(SECTIONS);

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * Replace the contents of each live section host with the freshly rendered markup.
   * innerHTML (rather than replacing the host itself) keeps the host element and its
   * delegated listeners alive across swaps.
   * @param {Object<string,string>} sections - `sections` map from an Ajax cart response.
   * @returns {boolean} whether anything was swapped in.
   */
  function applySections(sections) {
    if (!sections) return false;
    let applied = false;

    for (const sectionId of SECTION_IDS) {
      const html = sections[sectionId];
      if (typeof html !== 'string') continue;

      const hostTag = SECTIONS[sectionId];
      const live = document.querySelector(hostTag);
      if (!live) continue;

      const incoming = new DOMParser().parseFromString(html, 'text/html').querySelector(hostTag);
      if (!incoming) continue;

      // State the host carries for its own component (cart-totals stashes the
      // discount amounts it needs to recompute totals against state).
      for (const attribute of Array.from(incoming.attributes)) {
        if (attribute.name.indexOf('data-') === 0) live.setAttribute(attribute.name, attribute.value);
      }
      if (sectionId === 'cart-totals') {
        const inputValue = live.querySelector('[data-discount-input]')?.value;
        const incomingInput = incoming.querySelector('[data-discount-input]');
        if (incomingInput && inputValue !== undefined) incomingInput.value = inputValue;
        const preserved = ['[data-cart-reward-codes]', 'native-gift-selection'].map((selector) => ({
          node: live.querySelector(selector), placeholder: incoming.querySelector(selector),
        })).filter(({ node, placeholder }) => node && placeholder);
        for (const { node, placeholder } of preserved) placeholder.replaceWith(node);
        live.replaceChildren(...incoming.childNodes);
      } else live.innerHTML = incoming.innerHTML;
      if (sectionId === 'cart-totals') {
        window._erj_?.renderCartRewardCodes?.(window.accountInfo || { codeRecords: [] });
      }
      applied = true;
    }

    return applied;
  }

  /**
   * Re-render the cart fragments without mutating anything.
   * @returns {Promise<boolean>} whether the swap happened.
   */
  async function refreshSections(options) {
    const owner = window.enrouteCart;
    if (!owner) return false;
    // A full cart refresh passes its revision so its own HTML read cannot invalidate it.
    const revision = options?.revision ?? owner.invalidate();
    try {
      if (!owner.isCurrentRevision(revision)) return false;
      const response = await fetch(`${routeRoot()}?sections=${SECTION_IDS.join(',')}`);
      if (!response.ok) return false;
      const sections = await response.json();
      if (!owner.isCurrentRevision(revision)) return false;
      return applySections(sections);
    } catch (error) {
      console.error('Failed to refresh cart sections:', error);
      return false;
    }
  }

  /**
   * POST to one of the cart Ajax endpoints, asking for the drawer fragments back in
   * the same response, and swap them in.
   * @param {string} endpoint - `change.js`, `update.js` or `add.js`.
   * @param {Object} body - Endpoint-specific payload.
   * @param {{withSections?: boolean, transaction?: object}} [options] - Pass `withSections: false` for the
   *   intermediate step of a multi-call flow (the variant switcher's remove-then-add),
   *   so the half-finished cart never gets painted.
   * @returns {Promise<{ok: boolean, data: ?Object, error: ?string}>}
   */
  async function mutateCart(endpoint, body, options) {
    const withSections = !options || options.withSections !== false;
    const owner = window.enrouteCart;
    // Include body parsing and section application in the tracked operation.
    const operation = async (revision) => {
      try {
        const payload = withSections
          ? Object.assign({}, body, { sections: SECTION_IDS, sections_url: window.location.pathname })
          : body;
        const response = await fetch(`${routeRoot()}cart/${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          const message = (data && (data.description || data.message)) || `HTTP error! status: ${response.status}`;
          return { ok: false, data, error: message };
        }

        if (owner?.isCurrentRevision(revision) && data?.sections) applySections(data.sections);
        return { ok: true, data, error: null };
      } catch (error) {
        console.error(`Cart ${endpoint} failed:`, error);
        return { ok: false, data: null, error: (error && error.message) || 'Network error' };
      }
    };
    return owner ? owner.trackCartMutation(operation, options?.transaction) : operation();
  }

  /**
   * Reconcile state and HTML together after a drawer mutation. Its response may
   * have raced another write, so read the authoritative cart instead of reusing it.
   */
  function syncCartState() {
    return window.enrouteCart?.requestCartUpdate?.();
  }

  /** Surface a cart error through the controller's existing toast method. */
  function showError(message) {
    if (typeof window.enrouteCart?.showCartError === 'function') {
      window.enrouteCart?.showCartError?.(message);
      return;
    }
    document.querySelector('add-success-toast')?.show(`\n\n${message || ''}`, 3000, { error: true });
  }

  window._erj_ = window._erj_ || {};
  window._erj_.cartSections = {
    SECTION_IDS,
    applySections,
    refreshSections,
    mutateCart,
    syncCartState,
    showError,
  };
})();

/** Native discount gift lookup and presentation. Cart writes belong to cartOffers. */
(() => {
  const dataset = document.currentScript?.dataset;
  const offersEnabled = dataset?.cartOffersEnabled === 'true';
  // Whitelisted code prefixes keep the gift selection alive when the offers
  // module is turned off; the theme setting holds them one per line or comma-separated.
  const whitelist = String(dataset?.cartOffersWhitelist || '')
    .split(/[\n,]+/).map((prefix) => prefix.trim().toUpperCase()).filter(Boolean);
  if (!offersEnabled && !whitelist.length) return;
  const api = window._erj_;
  const offers = api.cartOffers;
  const offerText = (key, values) => api.cartOfferText(key, values);
  let generation = 0;
  let controller;
  let state = { status: 'idle', gifts: [] };
  const pendingAutoAdds = new Map();
  let pendingClaims = 0;

  async function autoAddGift(gift, variant) {
    const claimed = offers.findGift(gift, window.enrouteCart?.state);
    if (claimed && offers.variantId(claimed.variant_id || claimed.id) === offers.variantId(variant.id)) return;
    if (pendingAutoAdds.has(gift.code)) return pendingAutoAdds.get(gift.code);
    const pending = offers.apply(gift.code, variant.id);
    pendingAutoAdds.set(gift.code, pending);
    try { await pending; }
    finally { pendingAutoAdds.delete(gift.code); }
  }

  function publish(next) {
    state = next;
    window.dispatchEvent(new CustomEvent('enroute:native-gifts-updated'));
  }

  // The selection renders below the discount input, inside the scrollable drawer:
  // take focus when a fresh selection appears or the customer never notices it.
  function revealSelection() {
    const el = document.querySelector?.('native-gift-selection');
    if (!el || el.hidden || !state.gifts.length) return;
    el.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    el.querySelector('[data-native-gift-toggle]')?.focus?.({ preventScroll: true });
  }

  function clear({ preservePending = false } = {}) {
    // A cart refresh can overlap the rule request before an automatic claim
    // exists. Keep that lookup alive too, so its response can start the claim.
    if (preservePending && (state.status === 'loading' || pendingClaims || pendingAutoAdds.size)) return;
    generation++;
    controller?.abort();
    publish({ status: 'idle', gifts: [] });
  }

  async function update(discountCodes) {
    clear();
    const current = generation;
    const codes = [...new Set(discountCodes.map(({ code }) => code?.trim().toUpperCase()).filter(Boolean))];
    if (!codes.length) return { autoAdded: [] };
    if (!api.nativeGiftDiscountEndpoint) {
      publish({ status: 'unconfigured', gifts: [] });
      return { autoAdded: [] };
    }
    controller = new AbortController();
    const requestController = controller;
    const timeout = setTimeout(() => requestController.abort(), 10000);
    publish({ status: 'loading', gifts: [] });
    const autoAdded = [];
    try {
      const gifts = await offers.lookup(codes, requestController.signal);
      if (current !== generation) return;
      const selectableGifts = [];
      for (const gift of gifts) {
        if (current !== generation) return;
        const availableVariants = [...new Map(gift.products.flatMap((product) =>
          product.variants.filter((variant) => variant.available).map((variant) => [offers.variantId(variant.id), variant])
        )).values()];
        if (availableVariants.length !== 1) {
          selectableGifts.push(gift);
          continue;
        }
        publish({ status: 'adding', gifts: [] });
        try {
          await autoAddGift(gift, availableVariants[0]);
          autoAdded.push(gift.code);
        }
        catch (error) { if (current === generation) api.cartSections?.showError?.(error.message); }
      }
      if (current === generation) {
        publish({ status: 'ready', gifts: selectableGifts });
        revealSelection();
      }
    } catch (error) {
      if (current === generation) publish({ status: 'error', gifts: [] });
    } finally {
      clearTimeout(timeout);
    }
    return { autoAdded };
  }

  // With offers disabled, only whitelisted prefixes may route a code into the
  // gift lookup; every other code keeps the plain discount/gift-card flow.
  function intercepts(code) {
    if (offersEnabled) return true;
    const value = String(code || '').trim().toUpperCase();
    return whitelist.some((prefix) => value.startsWith(prefix));
  }

  api.nativeGiftSelection = { update, clear, intercepts, get state() { return state; } };

  class NativeGiftSelection extends HTMLElement {
    connectedCallback() {
      this._render = this.render.bind(this);
      this.querySelector('[data-native-gift-cancel]')?.addEventListener('click', this._cancel ||= this.cancel.bind(this));
      window.addEventListener('enroute:native-gifts-updated', this._render);
      this.render();
    }

    disconnectedCallback() {
      this._pickers?.forEach((picker) => picker.close());
      this.querySelector('[data-native-gift-cancel]')?.removeEventListener('click', this._cancel);
      window.removeEventListener('enroute:native-gifts-updated', this._render);
    }

    async cancel() {
      const button = this.querySelector('[data-native-gift-cancel]');
      if (!button || button.disabled || offers.busy || !state.gifts.length) return;
      const codes = state.gifts.map((gift) => gift.code);
      button.disabled = true;
      this._pickers?.forEach((picker) => picker.close());
      try {
        await offers.cancel(codes);
        clear();
        window.dispatchEvent(new CustomEvent('enroute:cart-offer-cancelled', { detail: { codes } }));
      } catch (error) {
        api.cartSections?.showError?.(error.message);
      } finally {
        button.disabled = false;
      }
    }

    render() {
      const list = this.querySelector('[data-native-gift-list]');
      const template = this.querySelector('[data-native-gift-row]');
      const skeleton = this.querySelector('[data-native-gift-skeleton]');
      if (!list || !template) return;
      this.hidden = !state.gifts.length;
      if (skeleton) skeleton.hidden = state.status !== 'loading';
      this.setAttribute('aria-busy', String(state.status === 'loading'));
      const cancel = this.querySelector('[data-native-gift-cancel]');
      if (cancel) cancel.disabled = state.status === 'loading' || offers.busy;
      const signature = JSON.stringify(state);
      if (this._giftRenderSignature === signature) {
        list.querySelectorAll('.gift-selection-row').forEach((card) => card._syncNativeGiftFromCart?.());
        return;
      }
      this._giftRenderSignature = signature;
      this._pickers?.forEach((picker) => picker.destroy());
      this._pickers = [];
      list.replaceChildren();
      for (const gift of state.gifts) {
        const row = template.content.cloneNode(true);
        const card = row.querySelector('.gift-selection-row');
        const title = row.querySelector('[data-native-gift-title]');
        const status = row.querySelector('[data-native-gift-status]');
        const confirm = row.querySelector('[data-native-gift-confirm]');
        const toggle = row.querySelector('[data-native-gift-toggle]');
        const selectedLabel = row.querySelector('[data-native-gift-selected]');
        let cartGift = offers.findGift(gift, window.enrouteCart?.state);
        let selectedVariant;
        let busy = false;
        title.textContent = gift.products.length === 1 ? gift.products[0].title : offerText('choose_gift');
        const updateClaimButton = () => {
          const unchanged = cartGift && offers.variantId(selectedVariant?.id) === offers.variantId(cartGift.variant_id || cartGift.id);
          confirm.disabled = busy || !selectedVariant?.available || unchanged;
        };
        const picker = offers.createPicker({
          rule: gift, toggle, label: selectedLabel,
          switcher: row.querySelector('.gift-variant-switcher'),
          popup: row.querySelector('[data-native-gift-options]'),
          image: row.querySelector('[data-native-gift-image]'),
          selectedId: cartGift && (cartGift.variant_id || cartGift.id),
          isBusy: () => busy || offers.busy,
          onChange: (product, variant) => { selectedVariant = variant; title.textContent = product.title; updateClaimButton(); },
        });
        this._pickers.push(picker);
        card._syncNativeGiftFromCart = () => {
          if (busy) return;
          cartGift = offers.findGift(gift, window.enrouteCart?.state);
          const id = cartGift && (cartGift.variant_id || cartGift.id);
          if (id && offers.variantId(id) !== offers.variantId(selectedVariant?.id)) picker.select(id);
          updateClaimButton();
        };
        if (!picker.available) {
          toggle.disabled = true;
          confirm.disabled = true;
          card.classList.add('is-unavailable');
          status.textContent = offerText('sold_out');
          if (!picker.selected) selectedLabel.textContent = offerText('sold_out');
        }
        confirm.addEventListener('click', async () => {
          if (confirm.disabled || busy || offers.busy || !selectedVariant?.available) return;
          const claimGeneration = generation;
          pendingClaims++;
          busy = true;
          toggle.disabled = true;
          card.classList.add('is-loading');
          confirm.disabled = true;
          confirm.dataset.loading = 'true';
          confirm.setAttribute('aria-busy', 'true');
          confirm.querySelector('.loader-dot-hvg-white').hidden = false;
          try {
            if (cartGift) {
              await offers.changeVariant(gift.code, cartGift.variant_id || cartGift.id, selectedVariant.id);
              status.textContent = offerText('gift_updated');
              window.dispatchEvent(new CustomEvent('enroute:cart-offer-success', { detail: { message: offerText('gift_variant_updated') } }));
            } else {
              await offers.apply(gift.code, selectedVariant.id);
              status.textContent = offerText('gift_added');
              window.dispatchEvent(new CustomEvent('enroute:cart-offer-success', { detail: { message: offerText('gift_added_offer_applied') } }));
              // The code typed into the discount input has served its purpose; clear it
              // and let cart-discount-form sync its apply-button state.
              const input = document.querySelector('cart-discount-form [data-discount-input]');
              if (input) {
                input.value = '';
                input.dispatchEvent(new Event('input', { bubbles: true }));
              }
            }
            cartGift = { variant_id: selectedVariant.id };
            if (claimGeneration === generation) {
              publish({ status: 'ready', gifts: state.gifts.filter((item) => item !== gift) });
            }
          } catch (error) {
            api.cartSections?.showError?.(error.message);
          } finally {
            pendingClaims--;
            busy = false;
            toggle.disabled = !picker.available;
            card.classList.remove('is-loading');
            delete confirm.dataset.loading;
            confirm.setAttribute('aria-busy', 'false');
            confirm.querySelector('.loader-dot-hvg-white').hidden = true;
            updateClaimButton();
          }
        });
        list.appendChild(row);
      }
    }
  }

  if (!customElements.get('native-gift-selection')) customElements.define('native-gift-selection', NativeGiftSelection);
})();

/**
 * Cart drawer line-item interactions: quantity stepper, remove flow and the
 * variant switcher. PopupCartItems extends the shared CartItems base.
 *
 * Replaces the Vue methods `updateQ`, `removeItemFromCart`, `moveItemToWishlist`,
 * `cancelRemoveOperation`, `toggleCartItemSwither`, `setSwitchOption`,
 * `isOptionAvailable`, `isOptionSelected`, `setSwitchPossibleValues` and
 * `closeAllSwitcherPopups` from assets/main.js.
 *
 * The markup is server-rendered by snippets/cart-items.liquid; everything this
 * component needs (the product's options, its variants and their availability, the
 * line's properties) arrives as data-* attributes, so no per-line-item
 * `products/{handle}.js` request is made any more. Listeners are delegated from the
 * host element so they survive a Section Rendering API swap replacing its innerHTML.
 */
(function () {
  if (customElements.get('cart-items')) return;

  /** Matches the debounce the old Vue `updateQ` used before hitting cart/change.js. */
  const QUANTITY_DEBOUNCE_MS = 400;

  function cartSections() {
    return window._erj_ && window._erj_.cartSections;
  }


  function normalizeProperties(properties) {
    if (Array.isArray(properties)) {
      try {
        properties = Object.fromEntries(properties);
      } catch (error) {
        return '{}';
      }
    }
    if (!properties || typeof properties !== 'object') return '{}';
    const normalized = {};
    for (const key of Object.keys(properties).sort()) {
      normalized[key] = properties[key];
    }
    return JSON.stringify(normalized);
  }

  /** Shared variant picker and remove/add transaction for cart item views.
   * Subclasses extend quantity rendering, cart events and selection state.
   */
  class CartItems extends HTMLElement {
    constructor() {
      super();
      this._quantityTimers = new Map();
      this._pendingQuantities = new Map();
      this._onKeydown = this._onKeydown.bind(this);
      this._onDocumentClick = this._onDocumentClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('keydown', this._onKeydown);
      // Capture also sees outside clicks whose handlers stop propagation.
      document.addEventListener('click', this._onDocumentClick, true);
    }

    disconnectedCallback() {
      this.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('click', this._onDocumentClick, true);
      for (const timer of this._quantityTimers.values()) clearTimeout(timer);
      this._quantityTimers.clear();
      this._pendingQuantities.clear();
    }

    _onDocumentClick(event) {
      if (!this.contains(event.target)) this._closeAllSwitchers();
    }

    _onKeydown(event) {
      if (event.key !== 'Escape') return;
      const open = this.querySelector('[data-variant-switcher-popup]:not([hidden])');
      if (!open) return;
      event.stopPropagation();
      this._closeAllSwitchers();
      open.closest('[data-variant-switcher]')?.querySelector('[data-variant-switcher-toggle]')?.focus();
    }

    _cancelPendingQuantity(key) {
      clearTimeout(this._quantityTimers.get(key));
      this._quantityTimers.delete(key);
      this._pendingQuantities.delete(key);
    }

    _stepQuantity(item, delta) {
      if (!item || !item.dataset.lineKey) return;
      const current = Number(item.dataset.quantity) || 0;
      const next = current + delta;
      if (next < 0 || next === current) return;

      if (next === 0) {
        const properties = window._erj_.parseJSONAttribute(item, 'data-properties', {});
        if (!properties._bundle_id) {
          document.querySelector('cart-remove-dialog')?.show({
            key: item.dataset.lineKey,
            handle: item.dataset.handle,
            productId: item.dataset.productId,
            variantId: item.dataset.variantId,
          });
          return;
        }
      }

      this._updateQuantityDisplay(item, next, current);
      const key = item.dataset.lineKey;
      this._cancelPendingQuantity(key);
      this._pendingQuantities.set(key, next);
      this._quantityTimers.set(key, setTimeout(() => {
        this._quantityTimers.delete(key);
        this._pendingQuantities.delete(key);
        this._commitQuantity(key, next);
      }, QUANTITY_DEBOUNCE_MS));
    }

    _updateQuantityDisplay(item, next, current) {
      item.dataset.quantity = String(next);
      const value = item.querySelector('[data-qty-value]');
      if (value) value.textContent = String(next);
    }

    _getCartLine(switcher) {
      return switcher.closest('[data-cart-item]');
    }

    _beforeVariantSwitch(item, variantId) {
      this._cancelPendingQuantity(item.dataset.lineKey);
    }
    _beforeVariantAdd(item, variantId) {}
    _onVariantAddFailed() {}

    _closeAllSwitchers(except) {
      for (const popup of this.querySelectorAll('[data-variant-switcher-popup]')) {
        if (except && popup === except) continue;
        popup.hidden = true;
        popup.closest('[data-variant-switcher]')?.querySelector('[data-variant-switcher-toggle]')?.setAttribute('aria-expanded', 'false');
      }
    }

    _toggleSwitcher(switcher) {
      if (!switcher) return;
      const popup = switcher.querySelector('[data-variant-switcher-popup]');
      if (!popup) return;
      const willOpen = popup.hidden;
      this._closeAllSwitchers(popup);
      popup.hidden = !willOpen;
      switcher.querySelector('[data-variant-switcher-toggle]')?.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) {
        this._paintSwitcherState(switcher);
        if (typeof popup.reposition === 'function') popup.reposition();
      }
    }

    /**
     * Which values are still reachable on each axis, given the selections already made
     * on the axes before it. Direct port of Vue's `setSwitchPossibleValues`.
     * @param {Array<{id:number, options:Array<?string>, available:boolean}>} variants
     * @param {Array<?string>} selected - Current selection per axis.
     * @returns {Array<Array<string>>} Reachable values per axis.
     */
    _possibleValues(variants, selected) {
      const available = variants.filter((variant) => variant.available);
      const axes = [[], [], []];

      for (const variant of available) {
        if (variant.options[0] != null && !axes[0].includes(variant.options[0])) axes[0].push(variant.options[0]);
      }
      if (selected[0] != null) {
        for (const variant of available) {
          if (variant.options[0] !== selected[0]) continue;
          if (variant.options[1] != null && !axes[1].includes(variant.options[1])) axes[1].push(variant.options[1]);
        }
      }
      if (selected[0] != null && selected[1] != null) {
        for (const variant of available) {
          if (variant.options[0] !== selected[0] || variant.options[1] !== selected[1]) continue;
          if (variant.options[2] != null && !axes[2].includes(variant.options[2])) axes[2].push(variant.options[2]);
        }
      }
      return axes;
    }

    /** Mark each option value as selected/available for the switcher's current state. */
    _paintSwitcherState(switcher) {
      const variants = window._erj_.parseJSONAttribute(switcher, 'data-variants', []);
      const selected = window._erj_.parseJSONAttribute(switcher, 'data-selected', [null, null, null]);
      const reachable = this._possibleValues(variants, selected);

      for (const optionEl of switcher.querySelectorAll('[data-variant-option]')) {
        const axis = (Number(optionEl.dataset.optionPosition) || 1) - 1;
        const value = optionEl.dataset.optionValue;
        const isAvailable = reachable[axis] ? reachable[axis].includes(value) : false;
        const isSelected = selected[axis] === value;

        // data-variant-option is on the whole row (check zone is clickable too);
        // the name/disabled look lives on the inner name node.
        const nameEl = optionEl.querySelector(
          '.hvg-cart-variant-switcher-popup-optionblock-item-name, .hvg-cart-variant-switcher-popup-optionblock-item-name-disabled'
        );
        if (nameEl) {
          nameEl.classList.toggle('hvg-cart-variant-switcher-popup-optionblock-item-name', isAvailable);
          nameEl.classList.toggle('hvg-cart-variant-switcher-popup-optionblock-item-name-disabled', !isAvailable);
        }
        optionEl.toggleAttribute('data-disabled', !isAvailable);
        optionEl.classList.toggle('is-selected', isSelected);

        const check = optionEl.querySelector('[data-variant-option-check]');
        if (check) check.hidden = !isSelected;
      }
    }

    _onVariantOptionClick(optionEl) {
      const switcher = optionEl.closest('[data-variant-switcher]');
      if (!switcher) return;

      const variants = window._erj_.parseJSONAttribute(switcher, 'data-variants', []);
      const selected = window._erj_.parseJSONAttribute(switcher, 'data-selected', [null, null, null]);
      const axis = (Number(optionEl.dataset.optionPosition) || 1) - 1;
      const value = optionEl.dataset.optionValue;

      // Disabled values are inert, same as the old `v-if="isOptionAvailable(...)"` split.
      const reachable = this._possibleValues(variants, selected);
      if (!reachable[axis] || !reachable[axis].includes(value)) return;

      selected[axis] = value;
      switcher.setAttribute('data-selected', JSON.stringify(selected));
      this._paintSwitcherState(switcher);

      const options = window._erj_.parseJSONAttribute(switcher, 'data-options', []);
      const targetVariant = this._resolveVariant(variants, options, selected);
      if (!targetVariant) return;

      const item = this._getCartLine(switcher);
      if (!item || String(targetVariant.id) === String(item.dataset.variantId)) return;

      this._switchVariant(item, switcher, targetVariant.id);
    }

    /**
     * A variant is only resolvable once every axis the product actually has is chosen
     * and the combination narrows to exactly one variant. Mirrors Vue's `setSwitchOption`.
     */
    _resolveVariant(variants, options, selected) {
      const axisCount = options.length;
      for (let axis = 0; axis < axisCount; axis += 1) {
        if (selected[axis] == null) return null;
      }
      const matches = variants.filter((variant) => {
        for (let axis = 0; axis < axisCount; axis += 1) {
          if (variant.options[axis] !== selected[axis]) return false;
        }
        return true;
      });
      return matches.length === 1 ? matches[0] : null;
    }

    /**
     * Shopify's cart has no "change this line's variant" operation, so the old line is
     * zeroed out and the new variant re-added with the same quantity and properties —
     * the same two-step Vue used. The new line lands at the end of the cart; the Vue
     * version used to pin it back to its original position via `cartSort`, which the
     * server-rendered list intentionally no longer does.
     */
    async _switchVariant(item, switcher, variantId) {
      const sections = cartSections();
      if (!sections) return;

      this._beforeVariantSwitch(item, variantId);

      const loader = switcher.querySelector('[data-variant-switcher-loader]');
      const info = switcher.querySelector('[data-variant-switcher-toggle]');
      const popup = switcher.querySelector('[data-variant-switcher-popup]');
      if (popup) popup.hidden = true;
      if (loader) loader.hidden = false;
      if (info) { info.hidden = true; info.setAttribute('aria-expanded', 'false'); }

      const quantity = Number(item.dataset.quantity) || 1;
      const properties = window._erj_.parseJSONAttribute(item, 'data-properties', {});

      // Skip the intermediate render so the drawer never paints the removed-but-not-
      // yet-re-added state.
      const removed = await sections.mutateCart(
        'change.js',
        { id: item.dataset.lineKey, quantity: 0 },
        { withSections: false }
      );
      if (!removed.ok) {
        sections.showError(removed.error);
        if (loader) loader.hidden = true;
        if (info) info.hidden = false;
        return;
      }

      this._beforeVariantAdd(item, variantId);
      const added = await sections.mutateCart('add.js', {
        items: [{ id: variantId, quantity, properties }],
      });
      if (!added.ok) {
        this._onVariantAddFailed();
        sections.showError(added.error);
        await sections.refreshSections();
      }
      sections.syncCartState(null);
    }
  }

  window._erj_ = window._erj_ || {};
  window._erj_.CartItems = CartItems;

  class PopupCartItems extends CartItems {
    constructor() {
      super();
      this._optimisticAdds = new Map();
      this._onClick = this._onClick.bind(this);
      this._onCartUpdated = this._onCartUpdated.bind(this);
    }

    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('click', this._onClick);
      window.addEventListener('enroute:cart-updated', this._onCartUpdated);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.removeEventListener('click', this._onClick);
      window.removeEventListener('enroute:cart-updated', this._onCartUpdated);
      this._optimisticAdds.clear();
    }

    _onClick(event) {
      const target = event.target;

      const optionButton = target.closest('[data-variant-option]');
      if (optionButton) {
        event.preventDefault();
        event.stopPropagation();
        this._onVariantOptionClick(optionButton);
        return;
      }

      const switcherToggle = target.closest('[data-variant-switcher-toggle]');
      if (switcherToggle) {
        event.preventDefault();
        event.stopPropagation();
        this._toggleSwitcher(switcherToggle.closest('[data-variant-switcher]'));
        return;
      }

      this._closeAllSwitchers();

      const decrease = target.closest('[data-qty-decrease]');
      if (decrease) {
        event.preventDefault();
        this._stepQuantity(decrease.closest('[data-cart-item]'), -1);
        return;
      }

      const increase = target.closest('[data-qty-increase]');
      if (increase) {
        event.preventDefault();
        this._stepQuantity(increase.closest('[data-cart-item]'), 1);
        return;
      }

    }

    /* --------------------------------------------------------- optimistic add */

    _onCartUpdated(event) {
      const detail = event && event.detail;
      if (!detail || !detail.optimistic) {
        // A successful section swap disconnects every node recorded below. Drop
        // those completed handles while retaining any newer optimistic add that
        // was painted after the swap and is still awaiting its own response.
        for (const [id, handles] of this._optimisticAdds) {
          const stillVisible = handles.some((handle) => handle.node && this.contains(handle.node));
          if (!stillVisible) this._optimisticAdds.delete(id);
        }
        return;
      }

      const optimisticId = detail.optimisticId;
      if (optimisticId == null) return;

      if (detail.rollback) {
        const handles = this._optimisticAdds.get(optimisticId) || [];
        for (let index = handles.length - 1; index >= 0; index--) {
          this._revertOptimisticAdd(handles[index]);
        }
        this._optimisticAdds.delete(optimisticId);
        return;
      }

      if (this._optimisticAdds.has(optimisticId)) return;
      const handles = (Array.isArray(detail.items) ? detail.items : [])
        .map((item) => this._paintOptimisticAdd(item))
        .filter(Boolean);
      this._optimisticAdds.set(optimisticId, handles);
    }

    _paintOptimisticAdd(payloadItem) {
      if (!payloadItem || !payloadItem.id) return null;

      const quantity = Number(payloadItem.quantity) || 1;
      const variantId = String(payloadItem.id);
      const properties = normalizeProperties(payloadItem.properties);
      const existing = Array.from(
        this.querySelectorAll('[data-cart-item]:not([data-optimistic-line])')
      ).find((item) => {
        if (item.dataset.variantId !== variantId) return false;
        return normalizeProperties(window._erj_.parseJSONAttribute(item, 'data-properties', {})) === properties;
      });

      if (existing) {
        const next = (Number(existing.dataset.quantity) || 0) + quantity;
        existing.dataset.quantity = String(next);
        const valueEl = existing.querySelector('[data-qty-value]');
        if (valueEl) valueEl.textContent = String(next);
        return { type: 'quantity', node: existing, quantity };
      }

      const list = this.querySelector('.hvg-top-list');
      if (!list) return null;

      const template = document.createElement('template');
      template.innerHTML = `
        <li class="hvg-list-cart-item" data-optimistic-line aria-busy="true">
          <div class="grid">
            <a class="thumb"></a>
            <div class="info">
              <div class="grid">
                <div class="cart-item-info">
                  <div class="cart-item-title">
                    <a class="cart-item-title-title"></a>
                  </div>
                </div>
              </div>
              <div class="grid">
                <div class="cart-switcher-row">
                  <div class="cart-item-manipulator">
                    <span class="loader-dot-hvg"><i></i><i></i><i></i></span>
                  </div>
                  <div class="cart-switcher-row-q"><div class="val" data-qty-value></div></div>
                </div>
              </div>
            </div>
          </div>
        </li>`;
      const line = template.content.firstElementChild;
      line.dataset.quantity = String(quantity);
      line.dataset.variantId = variantId;
      line.setAttribute('data-properties', properties);
      const thumb = line.querySelector('.thumb');
      const title = line.querySelector('.cart-item-title a');
      title.textContent = payloadItem.title || '';
      if (payloadItem.url) {
        thumb.href = String(payloadItem.url);
        title.href = String(payloadItem.url);
      }
      if (payloadItem.image) thumb.style.backgroundImage = `url("${String(payloadItem.image).replace(/"/g, '\\"')}")`;
      const variantText = String(payloadItem.variantText || '').trim();
      if (variantText && variantText.toLowerCase() !== 'default title') {
        title.parentElement.insertAdjacentHTML('beforeend', '<span class="cart-item-title-tags"></span>');
        title.parentElement.lastElementChild.textContent = variantText;
      }
      line.querySelector('[data-qty-value]').textContent = String(quantity);
      list.appendChild(line);
      this._syncEmptyState();
      return { type: 'line', node: line, quantity };
    }

    _revertOptimisticAdd(handle) {
      if (!handle || !handle.node || !this.contains(handle.node)) return;
      if (handle.type === 'line') {
        handle.node.remove();
        this._syncEmptyState();
        return;
      }
      if (handle.type !== 'quantity') return;

      const next = Math.max((Number(handle.node.dataset.quantity) || 0) - handle.quantity, 0);
      handle.node.dataset.quantity = String(next);
      const valueEl = handle.node.querySelector('[data-qty-value]');
      if (valueEl) valueEl.textContent = String(next);
    }

    _syncEmptyState() {
      const emptyState = this.querySelector('.hvg-empty');
      const list = this.querySelector('.hvg-top-list');
      if (emptyState && list) emptyState.hidden = list.children.length > 0;
    }

    /* ------------------------------------------------------------------ quantity */

    async _commitQuantity(key, quantity) {
      const sections = cartSections();
      if (!sections) return;

      const result = await sections.mutateCart('change.js', { id: key, quantity });
      if (!result.ok) {
        sections.showError(result.error);
        // Re-render from the server so the optimistic number is rolled back.
        await sections.refreshSections();
      }
      sections.syncCartState(result.ok ? result.data : null);
    }

  }

  customElements.define('cart-items', PopupCartItems);

  /**
   * The "remove or move to wishlist?" confirmation. Replaces Vue's
   * `productRemoveDialog` state plus `removeItemFromCart` / `moveItemToWishlist` /
   * `cancelRemoveOperation`.
   */
  class CartRemoveDialog extends HTMLElement {
    constructor() {
      super();
      this._item = null;
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }

    /** @param {{key: string, handle: string, productId: string, variantId: string}} item */
    show(item) {
      this._item = item;
      this.hidden = false;
    }

    hide() {
      this._item = null;
      this.hidden = true;
    }

    _onClick(event) {
      if (event.target.closest('[data-remove-cancel]')) {
        event.preventDefault();
        this.hide();
        return;
      }
      if (event.target.closest('[data-remove-confirm]')) {
        event.preventDefault();
        this._removeItem();
        return;
      }
      if (event.target.closest('[data-remove-to-wishlist]')) {
        event.preventDefault();
        this._moveToWishlist();
      }
    }

    _moveToWishlist() {
      const item = this._item;
      const provider = window.wishlistProvider;
      if (item && provider) {
        provider.addItemToWishlist(
          { handle: item.handle, productId: item.productId, variantId: item.variantId },
          () => {},
          () => {}
        );
      }
      this._removeItem();
    }

    async _removeItem() {
      const item = this._item;
      this.hide();
      if (!item) return;

      const sections = cartSections();
      if (!sections) return;

      const result = await sections.mutateCart('change.js', { id: item.key, quantity: 0 });
      if (!result.ok) {
        sections.showError(result.error);
        return;
      }
      sections.syncCartState(result.data);
    }
  }

  if (!customElements.get('cart-remove-dialog')) {
    customElements.define('cart-remove-dialog', CartRemoveDialog);
  }
})();

/**
 * Everything in the cart drawer's totals area that Liquid cannot render.
 *
 * snippets/cart-totals.liquid server-renders the subtotal, the cart-level and
 * line-item discount pills and the total discount. Applied gift cards, supplemental discount codes and the estimated total come from
 * the Storefront Cart object (merged into `state` in assets/cart.js) and have no Liquid
 * equivalent, so they are filled in here whenever `enroute:cart-updated` fires.
 *
 * This component also owns removing an applied discount or gift card, replacing
 * Vue's `removeDiscountCodeFromCart` / `removeGiftCardFromCart`, and keeps the two
 * cart-derived bits of the drawer that sit outside the swapped sections in sync:
 * the header item count and the footer's estimated total.
 */
(function () {
  if (customElements.get('cart-totals')) return;

  function cartSections() {
    return window._erj_ && window._erj_.cartSections;
  }

  function formatMoney(cents) {
    if (!window.Shopify || typeof Shopify.formatMoney !== 'function') return '';
    return Shopify.formatMoney(cents, Shopify.money_format).replace(/<[^>]*>?/gm, '');
  }

  class CartTotals extends HTMLElement {
    constructor() {
      super();
      this._onCartUpdate = this._onCartUpdate.bind(this);
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);
      window.addEventListener('enroute:cart-updated', this._onCartUpdate);

      // A swap replaces this element's contents but the cart state behind it is
      // unchanged, so re-apply whatever state was last committed.
      this._render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      window.removeEventListener('enroute:cart-updated', this._onCartUpdate);
    }

    _onCartUpdate() {
      this._render();
      CartTotals.syncOutsideSections();
    }

    /* -------------------------------------------------------------- state fill */

    _render() {
      this._renderDiscounts(window.enrouteCart?.state, window.enrouteCart?.state?.summary);
    }

    _renderDiscounts(state, summary) {
      const block = this.querySelector('[data-discount-block]');
      const noDiscountBlock = this.querySelector('[data-no-discount-block]');
      const list = this.querySelector('[data-discount-list]');
      const amount = this.querySelector('[data-discount-amount]');
      if (!block || !noDiscountBlock || !list || !amount) return;

      // Drop anything a previous pass injected; the Liquid-rendered pills stay.
      for (const injected of list.querySelectorAll('[data-injected]')) injected.remove();

      const giftCards = (state && state.appliedGiftCards) || [];
      for (const giftCard of giftCards) {
        list.appendChild(this._pill(`...${giftCard.lastCharacters}`, 'data-remove-giftcard', giftCard.id));
      }

      // The server rendered a pill for every discount it could see. When it saw none
      // but the unified state reports applicable codes, show those instead.
      const serverPills = list.querySelectorAll('[data-remove-discount]').length;
      const graphqlCodes = (state && state.discountCodes) || [];
      const graphqlAllocations = (state && state.discountAllocations) || [];
      if (serverPills === 0 && graphqlCodes.length > 0 && graphqlAllocations.length > 0) {
        for (const discount of graphqlCodes) {
          const code = discount.code || '';
          list.appendChild(this._pill(`...${code.slice(-4)}`, 'data-remove-discount', code));
        }
      }

      const hasAny = list.children.length > 0;
      block.hidden = !hasAny;
      noDiscountBlock.hidden = hasAny;
      if (!hasAny) return;

      amount.textContent = `- ${this._discountAmount(summary)}`;
    }

    /**
     * Mirrors the old three-way amount branch (Vue's `sumWithGiftcardAndPromo` and the
     * `state.summary.general_formated` / `state.total_discount_formated` fallbacks).
     */
    _discountAmount(summary) {
      const productDiscountTotal = Number(this.dataset.productDiscountTotal) || 0;
      const totalDiscount = Number(this.dataset.totalDiscount) || 0;

      if (summary && productDiscountTotal > 0) return formatMoney(totalDiscount + summary.general * 100);
      if (summary) return summary.general_formated || formatMoney(0);
      return formatMoney(totalDiscount);
    }

    _pill(label, removeAttribute, removeValue) {
      const template = document.createElement('template');
      template.innerHTML = `
        <div class="hvg-cart-discount-item" data-injected>
          <span class="hvg-discount-item-applied"></span>
          <div class="hvg-closesymbol">&times;</div>
        </div>`;
      const pill = template.content.firstElementChild;
      pill.querySelector('.hvg-discount-item-applied').textContent = ` ${label} `;
      pill.querySelector('.hvg-closesymbol').setAttribute(removeAttribute, removeValue);
      return pill;
    }

    /* ------------------------------------------------------- discount / gift card */

    _onClick(event) {
      const discountClose = event.target.closest('[data-remove-discount]');
      if (discountClose) {
        event.preventDefault();
        this._removeDiscountCode(discountClose.getAttribute('data-remove-discount'));
        return;
      }
      const giftCardClose = event.target.closest('[data-remove-giftcard]');
      if (giftCardClose) {
        event.preventDefault();
        this._removeGiftCard();
      }
    }

    // Both mutations still live on the Vue instance because CartDiscountForm's
    // gift-card path calls removeDiscountCodeFromCart too (assets/global.js), so
    // reimplementing them here would fork the same Storefront mutation in two
    // places. They already refresh the drawer: each passes requestCartUpdate as its
    // fetchGraphQL callback, which now re-renders the sections.
    _removeDiscountCode(discountCode) {
      window.enrouteCart?.removeDiscountCodeFromCart?.(discountCode);
    }

    _removeGiftCard() {
      window.enrouteCart?.removeGiftCardFromCart?.();
    }

    /* ------------------------------------------------ bits outside the swapped DOM */

    /**
     * The drawer header's item count and the footer (estimated total + checkout) sit
     * outside <cart-items>/<cart-totals>, so a section swap never touches them.
     */
    static syncOutsideSections() {
      if (!window.enrouteCart?.state) return;

      for (const counter of document.querySelectorAll('[data-cart-item-count]')) {
        counter.textContent = String(window.enrouteCart.state.item_count ?? 0);
      }

      const footer = document.querySelector('[data-cart-footer]');
      if (footer) footer.hidden = !window.enrouteCart.state.item_count;

      const total = document.querySelector('[data-cart-estimated-total]');
      if (total) {
        const state = window.enrouteCart.state;
        const estimatedTotal = state.summary?.totalAmount;
        const shipping = state.summary?.shipping ? Number(state.summary.shippingCost) : 0;
        const excludedCents = Math.round((Number.isFinite(shipping) ? shipping : 0) * 100);
        // Leave shipping for checkout, preserving other adjustments.
        total.textContent = estimatedTotal?.amount != null && Number.isFinite(Number(estimatedTotal.amount))
          ? formatMoney(Math.max(0, Math.round(Number(estimatedTotal.amount) * 100) - excludedCents))
          : state.total || formatMoney(state.total_price || 0);
      }
    }
  }

  customElements.define('cart-totals', CartTotals);
})();

/** Two-panel touch container. Configure business callbacks before selecting a tab.
 * Mark panels, labels and the content stack with data-swipe-panel/tab/stack.
 * The root owns the data-dragging styles; this container owns touch listeners.
 */
class SwipeTabs extends HTMLElement {
  constructor() {
    super();
      this._swipe = null;          // legacy commit-based swipe state (weak devices)
      this._follow = null;         // active follow-drag state (see _onTouchStart)
      this._followGroups = null;   // cached panel pair while transitioning
      this._followRaf = 0;         // settle/commit tween handle
      this._followPos = 0;         // last rendered pos (mid-tween pickup point)
      this._followTarget = null;   // where the running tween is heading
      this._followCapableCache = null;
      this._onTouchStart = this._onTouchStart.bind(this);
      this._onTouchMove = this._onTouchMove.bind(this);
      this._onTouchEnd = this._onTouchEnd.bind(this);
      this._onTouchCancel = this._onTouchCancel.bind(this);
  }

  configure(options) {
    this._options = options;
    this._root = options.root;
  }

  get _firstActive() { return this._options?.isFirst() ?? true; }

  connectedCallback() {
    this.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this.addEventListener('touchmove', this._onTouchMove, { passive: true });
    this.addEventListener('touchend', this._onTouchEnd);
    this.addEventListener('touchcancel', this._onTouchCancel);
  }

  disconnectedCallback() {
    this.reset();
    this.removeEventListener('touchstart', this._onTouchStart);
    this.removeEventListener('touchmove', this._onTouchMove);
    this.removeEventListener('touchend', this._onTouchEnd);
    this.removeEventListener('touchcancel', this._onTouchCancel);
  }

  get transitioning() { return Boolean(this._followRaf); }
  animateTo(target) { return this._startPushFade(target); }
  finish() { if (this._followRaf) this._finishFollow(this._followTarget); }
  reset() {
    this._swipe = null;
    this._follow = null;
    this._endFollow();
  }

    // Commit path shared by tab clicks, the legacy swipe and the follow drag:
    // push-fade from wherever the panels currently are to the target tab.
    // Returns false when it cannot run (groups missing or reduced motion), so
    // the caller swaps the mode instantly instead.
    _startPushFade(target) {
      if (this._reducedMotion()) return false;
      // Pickup point: if a previous tween is still running, continue from it.
      const from = this._followRaf ? this._followPos : (this._firstActive ? 0 : 1);
      if (!this._beginFollow()) return false;
      this._settleFollow(from, target);
      return true;
    }

    _reducedMotion() {
      try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      } catch (e) {
        return false;
      }
    }

    // ---- Swipe between bag / wishlist tabs ----
    //
    // Capable devices get an iOS-style follow drag: a continuous pos value in
    // [0, 1] (0 = bag, 1 = wishlist) maps the finger position 1:1 to the
    // push-fade transition (see _renderFollow), the tab underline and the tab
    // label colours, so it tracks the finger forward AND backward. On release,
    // pos >= 0.5 or a fast flick commits (tween to the other tab, then the
    // real mode applies); otherwise it springs back and the mode never changes.
    //
    // Weak devices (or reduced-motion users) skip the finger tracking and keep
    // the cheaper commit-based swipe: nothing renders until touchend, then the
    // same push-fade tween (or an instant swap under reduced motion) runs.

    _panelGroups() {
      if (!this._root) return null;
      const first = this._root.querySelector('[data-swipe-panel="first"]');
      const second = this._root.querySelector('[data-swipe-panel="second"]');
      return first && second ? { first, second } : null;
    }

    // Detected once on the first eligible touch. Static signals first
    // (reduced motion, single-core CPU, ≤2GB RAM buckets on Chromium), then a
    // runtime probe: the cost of one style write + forced reflow on the panel
    // group. Slow devices fail the probe and fall back to the legacy swipe.
    _followCapable() {
      if (this._followCapableCache != null) return this._followCapableCache;
      let capable = true;
      try {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) capable = false;
        const cores = navigator.hardwareConcurrency;
        const mem = navigator.deviceMemory;
        if (cores && cores < 2) capable = false;
        if (mem && mem <= 2) capable = false;
        const groups = this._panelGroups();
        if (capable && groups) {
          const t0 = performance.now();
          groups.first.style.opacity = '0.99';
          void groups.first.offsetWidth;
          groups.first.style.opacity = '';
          if (performance.now() - t0 > 8) capable = false;
        }
      } catch (e) {
        capable = false;
      }
      this._followCapableCache = capable;
      return capable;
    }

    _onTouchStart(e) {
      // Both tabs must exist for a swipe to mean anything.
      if (!this._options?.enabled() || !this.querySelector('[data-swipe-panel="second"]') || e.touches.length !== 1) {
        // A second finger can interrupt an already rendered horizontal drag.
        // Roll it back before discarding the gesture so panels regain pointers.
        this._onTouchCancel();
        return;
      }
      const t = e.touches[0];
      if (this._followCapable()) {
        this._swipe = null;
        const basePos = this._firstActive ? 0 : 1; // mode position the drag started from
        // If a click commit is still tweening, pick the drag up from where
        // the panels currently are instead of snapping back to the base.
        const originPos = this._followRaf ? this._followPos : basePos;
        this._follow = {
          startX: t.clientX,
          startY: t.clientY,
          axis: null,
          basePos,
          originPos,
          pos: originPos,
          samples: [{ x: t.clientX, t: e.timeStamp }],
        };
      } else {
        this._follow = null;
        this._swipe = { startX: t.clientX, startY: t.clientY, dx: 0, axis: null, at: e.timeStamp };
      }
    }

    _onTouchMove(e) {
      const f = this._follow;
      if (f) {
        const t = e.touches[0];
        const dx = t.clientX - f.startX;
        const dy = t.clientY - f.startY;
        if (!f.axis) {
          if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
          f.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (f.axis === 'x') {
            this._beginFollow();
            if (!this._follow) return; // _beginFollow bailed out; gesture over
          } else {
            this._follow = null; // vertical intent: let the list scroll
            return;
          }
        }
        f.samples.push({ x: t.clientX, t: e.timeStamp });
        if (f.samples.length > 24) f.samples.shift();
        // 1:1 finger tracking: pos crosses its whole range over one drawer
        // width, so the arriving page sticks to the finger.
        const range = this.clientWidth || 320;
        // Slight over-scroll allowance past [0, 1] feeds the rubber-band nudge.
        f.pos = Math.max(-0.25, Math.min(1.25, f.originPos - dx / range));
        this._renderFollow(f.pos);
        return;
      }

      const s = this._swipe;
      if (!s) return;
      const t = e.touches[0];
      s.dx = t.clientX - s.startX;
      if (!s.axis) {
        const dy = t.clientY - s.startY;
        // Lock the axis once intent is clear so vertical scrolling inside the
        // drawer never turns into a tab swipe.
        if (Math.abs(s.dx) >= 10 || Math.abs(dy) >= 10) {
          s.axis = Math.abs(s.dx) > Math.abs(dy) ? 'x' : 'y';
        }
      }
    }

    _onTouchEnd(e) {
      const f = this._follow;
      if (f) {
        this._follow = null;
        if (f.axis !== 'x') return; // never became a horizontal drag
        const p = Math.max(0, Math.min(1, f.pos));
        const v = this._dragVelocity(f.samples);
        let target;
        if (v < -0.45 && p > 0.02) target = 1;      // fast flick left → wishlist
        else if (v > 0.45 && p < 0.98) target = 0;  // fast flick right → bag
        else target = p >= 0.5 ? 1 : 0;             // otherwise the halfway rule
        if (target !== f.basePos) {
          // Committing: kick off wishlist/bag data work now so it overlaps
          // the settle tween instead of starting after it.
          this._options.prepare(target === 0);
        }
        this._settleFollow(f.pos, target);
        return;
      }

      const s = this._swipe;
      this._swipe = null;
      if (!s || s.axis !== 'x') return;

      // Commit on a long-enough swipe, or a short fast flick.
      const elapsed = Math.max(e.timeStamp - s.at, 1);
      const flick = Math.abs(s.dx) >= 24 && Math.abs(s.dx) / elapsed >= 0.35;
      if (Math.abs(s.dx) < 50 && !flick) return;

      const nextBag = s.dx > 0; // swipe right → bag tab, swipe left → wishlist tab
      if (nextBag === this._firstActive) return;
      this._options.select(nextBag);
    }

    _onTouchCancel() {
      this._swipe = null;
      const f = this._follow;
      this._follow = null;
      if (f && f.axis === 'x' && this._followGroups) {
        this._settleFollow(f.pos, f.basePos); // system stole the gesture: roll back
      } else {
        this._endFollow();
      }
    }

    // Average px/ms across the newest ≤90ms window, measured from the oldest
    // sample inside it. Spanning the window (not a single sample gap) keeps a
    // jittery last touchmove from faking a flick.
    _dragVelocity(samples) {
      if (!samples || samples.length < 2) return 0;
      const last = samples[samples.length - 1];
      let old = samples[0];
      for (const s of samples) {
        if (last.t - s.t <= 90) { old = s; break; }
      }
      if (last === old) return 0;
      const dt = last.t - old.t;
      // Sub-5ms spans carry no reliable velocity (also covers synthetic events
      // whose timestamps are all identical).
      if (dt < 5) return 0;
      return (last.x - old.x) / dt;
    }

    // Enter the both-groups transition state (data-dragging): cancels any
    // running tween, preserves the current mode and caches the
    // panel groups. Returns false when the groups are missing.
    _beginFollow() {
      const root = this._root;
      const groups = this._panelGroups();
      if (!root || !groups) {
        this._follow = null;
        return false;
      }
      // A previous transition may still be tweening; stop it. Callers either
      // re-render from the current position (drag/_startPushFade pickup) or
      // from rest, so nothing jumps.
      cancelAnimationFrame(this._followRaf);
      this._followRaf = 0;
      this._followGroups = groups;
      root.setAttribute('data-dragging', this._firstActive ? 'from-bag' : 'from-wishlist');
      return true;
    }

    // Push-fade: the arriving group slides in fully opaque from its own edge
    // (painting above), while the leaving group stays anchored and dissolves
    // toward the drawer background. d = progress away from the base page — p
    // runs 0→1 from bag but 1→0 from wishlist, so it must be normalised
    // before driving the layers or the wishlist→bag direction inverts.
    _renderFollow(posRaw) {
      const groups = this._followGroups;
      if (!groups) return;
      const p = Math.max(0, Math.min(1, posRaw));
      this._followPos = p;
      const baseIsBag = this._firstActive;
      const d = baseIsBag ? p : 1 - p;
      const leaving = baseIsBag ? groups.first : groups.second;
      const arriving = baseIsBag ? groups.second : groups.first;
      // The old page dissolves AHEAD of the drag progress (power curve), so it
      // reads as leaving sooner; raise the exponent for an even faster fade
      // (1 = the old linear behaviour).
      leaving.style.opacity = String(Math.pow(1 - d, 1.6));
      leaving.style.transform = 'translateX(0)';
      leaving.style.zIndex = '1';
      arriving.style.opacity = '1';
      arriving.style.transform = `translateX(${(baseIsBag ? 1 : -1) * (1 - d) * 100}%)`;
      arriving.style.zIndex = '2';
      // Underline + label colours track the absolute tab position p.
      this._root?.querySelector('[data-swipe-tab="first"]')?.style.setProperty('--tab-ink-x', `${p * 100}%`);
      this._setTabLabelMix(p);
      // Rubber band: overshoot nudges the whole content stack with damping.
      const stack = this._root?.querySelector('[data-swipe-stack]');
      if (stack) stack.style.transform = `translateX(${(p - posRaw) * 16}px)`;
    }

    // Tab label colour, lerped between the selected (#000) and idle (#d9d9d9)
    // states from the CSS. Removed on cleanup so .selectedcard rules take over.
    _setTabLabelMix(p) {
      const left = this._root?.querySelector('[data-swipe-tab="first"]');
      const right = this._root?.querySelector('[data-swipe-tab="second"]');
      if (!left || !right) return;
      const c = Math.round(p * 217);
      left.style.color = `rgb(${c},${c},${c})`;
      const d = Math.round((1 - p) * 217);
      right.style.color = `rgb(${d},${d},${d})`;
    }

    // rAF tween (easeOutCubic) from the release point to the settled target.
    _settleFollow(from, target) {
      cancelAnimationFrame(this._followRaf);
      this._followTarget = target;
      const t0 = performance.now();
      const dur = 170 + 190 * Math.abs(target - from);
      const step = (now) => {
        const k = Math.min((now - t0) / dur, 1);
        const eased = 1 - Math.pow(1 - k, 3);
        this._renderFollow(from + (target - from) * eased);
        if (k < 1) {
          this._followRaf = requestAnimationFrame(step);
        } else {
          this._followRaf = 0;
          this._finishFollow(target);
        }
      };
      this._followRaf = requestAnimationFrame(step);
    }

    _finishFollow(target) {
      const isBag = target === 0;
      this._endFollow();
      this._options.commit(isBag);
      this._options.updateTabs();
    }

    _endFollow() {
      cancelAnimationFrame(this._followRaf);
      this._followRaf = 0;
      this._followTarget = null;
      this._followPos = this._firstActive ? 0 : 1;
      if (this._followGroups) {
        this._followGroups.first.style.cssText = '';
        this._followGroups.second.style.cssText = '';
        this._followGroups = null;
      }
      const stack = this._root?.querySelector('[data-swipe-stack]');
      if (stack) stack.style.transform = '';
      const left = this._root?.querySelector('[data-swipe-tab="first"]');
      const right = this._root?.querySelector('[data-swipe-tab="second"]');
      if (left) {
        left.style.removeProperty('--tab-ink-x');
        left.style.removeProperty('color');
      }
      if (right) right.style.removeProperty('color');
      this._root?.removeAttribute('data-dragging');
    }

}
if (!customElements.get('swipe-tabs')) customElements.define('swipe-tabs', SwipeTabs);
